import { supabase } from '../supabase';
import { normalizeCrmError } from './crmErrors';
import { formatNumberAr } from './numberFormat';
import { toNumber, roundNumber, summarizeCrmTimeEntries, summarizeFromTotals, normalizeHoursPeriod, resolveHoursPeriod } from './crmHorasResumen';
import { fetchAllRows, fetchRowsByIds } from './supabasePagination';

// El resumen de horas vive en `crmHorasResumen.js` (lógica pura, con tests en
// tests/crm-horas-proyecto.test.mjs). Se reexporta para no tocar a los cuatro
// consumidores que ya lo importan desde acá.
export { summarizeCrmTimeEntries, summarizeFromTotals };

export const CRM_TICKET_CRITICALITIES = [
  { value: 'baja', label: 'Baja' },
  { value: 'media', label: 'Media' },
  { value: 'alta', label: 'Alta' },
  { value: 'critica', label: 'Critica' },
];

export const CRM_TASK_HISTORY_ENTITY_TYPE = 'crm_ticket_tasks';

function groupBy(rows, key) {
  return (rows || []).reduce((accumulator, row) => {
    const value = row?.[key];
    if (!value) return accumulator;
    accumulator[value] = accumulator[value] || [];
    accumulator[value].push(row);
    return accumulator;
  }, {});
}

function mergeTasksById(...taskCollections) {
  const mergedMap = new Map();

  taskCollections.flat().forEach((taskRow) => {
    if (!taskRow?.id) return;
    mergedMap.set(taskRow.id, { ...(mergedMap.get(taskRow.id) || {}), ...taskRow });
  });

  return Array.from(mergedMap.values());
}

function uniqueIds(values) {
  return [...new Set((values || []).filter(Boolean))];
}

// Estado operativo que se asigna solo (trigger en `clients`) a los proyectos de
// un cliente al inactivarlo. Oculta el proyecto en todos lados salvo el filtro
// "Inactivos" de CRM > Proyectos, y NO se revierte al reactivar el cliente:
// para volver a verlo hay que cambiarle el estado a mano.
export const CRM_PROJECT_STATUS_UNFINISHED = 'sin_terminar';

export function isCrmProjectUnfinished(project) {
  return String(project?.status || '').trim().toLowerCase() === CRM_PROJECT_STATUS_UNFINISHED;
}

// Un proyecto es "seleccionable" (visible para trabajar) cuando no está dado de
// baja, no quedó "sin terminar" y su cliente está activo. La baja es solo
// visual: los reportes siguen incluyendo estos proyectos. clientsById es
// opcional (mapa id -> cliente).
export function isCrmProjectSelectable(project, clientsById = null) {
  if (!project) return false;
  if (project.is_active === false) return false;
  if (isCrmProjectUnfinished(project)) return false;
  const client = clientsById?.[project.client_id];
  if (client && client.is_active === false) return false;
  return true;
}

// Filtra una lista de proyectos dejando solo los seleccionables, usando la
// lista de clientes para aplicar la cascada (cliente inactivo => proyecto oculto).
export function filterSelectableCrmProjects(projects, clients) {
  const clientsById = Object.fromEntries((clients || []).map((row) => [row.id, row]));
  return (projects || []).filter((project) => isCrmProjectSelectable(project, clientsById));
}

const DEFAULT_DASHBOARD_WORKDAYS = [1, 2, 3, 4, 5];
const DEFAULT_DAILY_TARGET_MINUTES = 480;

// Devuelve la fecha "hoy" (o la recibida) como YYYY-MM-DD en zona horaria local.
// Usar SIEMPRE este helper en lugar de new Date().toISOString().slice(0, 10),
// que devuelve la fecha UTC e imputa al dia siguiente despues de las 21:00 (ART).
export function getLocalDateKey(input = new Date()) {
  const date = input instanceof Date ? new Date(input) : new Date(input);
  if (Number.isNaN(date.getTime())) return '';
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 10);
}

function normalizeDashboardWorkdays(value) {
  if (!Array.isArray(value)) return [...DEFAULT_DASHBOARD_WORKDAYS];

  const normalized = value
    .map((day) => Number(day))
    .filter((day) => Number.isInteger(day) && day >= 1 && day <= 7);

  return normalized.length
    ? Array.from(new Set(normalized)).sort((left, right) => left - right)
    : [...DEFAULT_DASHBOARD_WORKDAYS];
}

function getIsoWeekday(dateKey) {
  const date = new Date(`${dateKey}T12:00:00`);
  const day = date.getDay();
  return day === 0 ? 7 : day;
}

function unwrapResponse(label, response) {
  if (response.error) {
    throw normalizeCrmError(response.error, `No se pudo resolver ${label}`);
  }
  return response.data;
}

function unwrapOptionalRows(label, response, relationName) {
  if (!response?.error) return response?.data || [];
  if (isMissingRelationError(response.error, relationName)) return [];
  throw normalizeCrmError(response.error, `No se pudo resolver ${label}`);
}

function isMissingColumnError(error, columnName) {
  const needle = String(columnName || '').toLowerCase();
  const shortNeedle = needle.split('.').pop();
  const code = String(error?.code || error?.error?.code || '').toUpperCase();
  const message = [error?.message, error?.details, error?.hint]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (!needle) return false;

  return Boolean(
    code === '42703'
    || code === 'PGRST204'
    || message.includes(`column "${needle}"`)
    || message.includes(`column '${needle}'`)
    || message.includes(`column ${needle}`)
    || message.includes(`column "${shortNeedle}"`)
    || message.includes(`column '${shortNeedle}'`)
    || message.includes(`column ${shortNeedle}`)
    || message.includes(`could not find the '${needle}' column`)
    || message.includes(`could not find the "${needle}" column`)
    || message.includes(`could not find the '${shortNeedle}' column`)
    || message.includes(`could not find the "${shortNeedle}" column`)
  );
}

function isMissingRelationError(error, relationName) {
  const needle = String(relationName || '').toLowerCase();
  const code = String(error?.code || error?.error?.code || '').toUpperCase();
  const status = Number(error?.status || error?.error?.status || 0);
  const message = [error?.message, error?.details, error?.hint]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (!needle) return false;
  if (code === '42P01' || code === 'PGRST202' || code === 'PGRST205') {
    return message.includes(needle) || status === 404;
  }
  if (status === 404 && message.includes(needle)) return true;
  if (message.includes('could not find the table') && message.includes(needle)) return true;
  if (message.includes('could not find the function') && message.includes(needle)) return true;
  if (message.includes(`relation "public.${needle}`)) return true;
  if (message.includes(`relation "${needle}`)) return true;

  return false;
}

function shouldFallbackCrmRpc(error, rpcName) {
  const needle = String(rpcName || '').toLowerCase();
  const status = Number(error?.status || error?.error?.status || 0);
  const code = String(error?.code || error?.error?.code || '').toUpperCase();
  const message = [error?.message, error?.details, error?.hint]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (isMissingRelationError(error, rpcName)) return true;
  if (status === 404 && (!needle || message.includes(needle) || message.includes('function'))) return true;
  if (code === 'PGRST202' || code === 'PGRST205') return true;
  if (needle && message.includes(`public.${needle}`)) return true;
  if (needle && message.includes(`/${needle}`)) return true;
  if (needle === 'crm_finalize_meeting' && message.includes('record "topic_row" is not assigned yet')) return true;
  if (needle === 'crm_create_meeting' && message.includes('todos los temas deben estar asociados a un ticket')) return true;
  if (needle === 'crm_finalize_meeting' && message.includes('todos los temas tratados deben estar asociados a un ticket')) return true;

  return false;
}

function stringifyHistoryValue(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function getTicketBoardOrderMigrationMessage() {
  return 'Falta la migracion 20260503_crm_ticket_board_order.sql en Supabase para usar el orden manual de tarjetas.';
}

async function resolveNextCrmTicketNumber(organizationId) {
  // Numerador atomico: el RPC toma un advisory lock por organizacion y calcula
  // MAX+1 dentro de la transaccion, evitando duplicados por concurrencia.
  // Firma canonica en ambas bases: crm_next_ticket_number(target_org uuid) -> integer.
  const rpcResponse = await supabase.rpc('crm_next_ticket_number', { target_org: organizationId });
  if (!rpcResponse.error && rpcResponse.data != null) {
    return Number(rpcResponse.data || 0);
  }

  // Fallback sin RPC: MAX+1 leido desde el cliente (sin lock, best-effort).
  const latestResponse = await supabase
    .from('crm_tickets')
    .select('ticket_number')
    .eq('organization_id', organizationId)
    .order('ticket_number', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (latestResponse.error) {
    throw normalizeCrmError(latestResponse.error, 'No se pudo calcular el siguiente ticket');
  }

  return Number(latestResponse.data?.ticket_number || 0) + 1;
}

async function createCrmTicketCompatible(payload) {
  const organizationId = payload?.organization_id;
  const projectId = payload?.project_id || null;
  const ticketTypeId = payload?.ticket_type_id || null;
  const actorProfileId = payload?.actor_profile_id || null;
  const createdAt = new Date().toISOString();

  if (!organizationId || !projectId || !ticketTypeId) {
    throw new Error('Faltan datos minimos para crear el ticket en modo compatibilidad');
  }

  const projectResponse = await supabase
    .from('crm_projects')
    .select('id, client_id')
    .eq('organization_id', organizationId)
    .eq('id', projectId)
    .maybeSingle();

  const projectRow = unwrapResponse('crmProjectCompatible', projectResponse);
  if (!projectRow?.id) {
    throw new Error('Proyecto no encontrado para este ticket');
  }

  const ticketTypeResponse = await supabase
    .from('crm_ticket_types')
    .select('id, category_id')
    .eq('organization_id', organizationId)
    .eq('id', ticketTypeId)
    .maybeSingle();

  const ticketTypeRow = unwrapResponse('crmTicketTypeCompatible', ticketTypeResponse);
  if (!ticketTypeRow?.id) {
    throw new Error('Tipo interno de ticket no encontrado');
  }

  const ticketNumber = await resolveNextCrmTicketNumber(organizationId);
  const baseRow = {
    organization_id: organizationId,
    ticket_number: ticketNumber,
    client_id: projectRow.client_id,
    project_id: projectId,
    project_stage_id: payload?.project_stage_id || null,
    ticket_type_id: ticketTypeId,
    category_id: payload?.category_id || ticketTypeRow.category_id || null,
    status_id: payload?.status_id || null,
    title: String(payload?.title || '').trim() || `Ticket ${ticketNumber}`,
    reported_at: payload?.reported_at || createdAt,
    reported_by_name: payload?.reported_by_name || null,
    reported_by_contact: payload?.reported_by_contact || null,
    description: String(payload?.description || '').trim() || 'Sin descripcion',
    budget_text: payload?.budget_text || null,
    design_text: payload?.design_text || null,
    resolution_text: payload?.resolution_text || null,
    contact_name: payload?.contact_name || null,
    contact_channel: payload?.contact_channel || null,
    criticality: payload?.criticality || 'media',
    priority_weight: Number(payload?.priority_weight ?? 50) || 50,
    board_sort_order: payload?.board_sort_order ?? ticketNumber * 1000,
    trello_card_pos: payload?.trello_card_pos ?? null,
    assigned_to: null,
    created_at: createdAt,
    created_by: actorProfileId,
    updated_by: actorProfileId,
  };

  const ticketVariants = [
    { ...baseRow, work_item_kind: payload?.work_item_kind || 'requerimiento', budget_amount: payload?.budget_amount ?? null },
    { ...baseRow, work_item_kind: payload?.work_item_kind || 'requerimiento' },
    baseRow,
    (() => {
      const { board_sort_order, trello_card_pos, ...legacyRow } = baseRow;
      return { ...legacyRow, work_item_kind: payload?.work_item_kind || 'requerimiento', budget_amount: payload?.budget_amount ?? null };
    })(),
    (() => {
      const { board_sort_order, trello_card_pos, ...legacyRow } = baseRow;
      return { ...legacyRow, work_item_kind: payload?.work_item_kind || 'requerimiento' };
    })(),
    (() => {
      const { board_sort_order, trello_card_pos, ...legacyRow } = baseRow;
      return legacyRow;
    })(),
  ];

  let createdTicket = null;
  let lastError = null;

  for (const row of ticketVariants) {
    const response = await supabase.from('crm_tickets').insert(row).select('id, ticket_number').single();
    if (!response.error) {
      createdTicket = response.data;
      break;
    }

    lastError = response.error;
    const missingBudgetAmount = Object.prototype.hasOwnProperty.call(row, 'budget_amount') && isMissingColumnError(response.error, 'budget_amount');
    const missingWorkItemKind = Object.prototype.hasOwnProperty.call(row, 'work_item_kind') && isMissingColumnError(response.error, 'work_item_kind');
    const missingBoardSortOrder = Object.prototype.hasOwnProperty.call(row, 'board_sort_order') && isMissingColumnError(response.error, 'board_sort_order');
    const missingTrelloCardPos = Object.prototype.hasOwnProperty.call(row, 'trello_card_pos') && isMissingColumnError(response.error, 'trello_card_pos');
    if (missingBudgetAmount || missingWorkItemKind || missingBoardSortOrder || missingTrelloCardPos) continue;

    throw normalizeCrmError(response.error, 'No se pudo crear el ticket');
  }

  if (!createdTicket?.id) {
    throw normalizeCrmError(lastError, 'No se pudo crear el ticket');
  }

  return createdTicket;
}

function shouldFallbackCrmTicketCreate(error) {
  return isMissingColumnError(error, 'budget_amount') || isMissingColumnError(error, 'work_item_kind');
}

export function calculateMinutesFromTimes(startTime, endTime) {
  const normalizedStart = String(startTime || '').trim();
  const normalizedEnd = String(endTime || '').trim();
  if (!normalizedStart || !normalizedEnd) return 0;

  const [startHour, startMinute] = normalizedStart.split(':').map((value) => Number(value || 0));
  const [endHour, endMinute] = normalizedEnd.split(':').map((value) => Number(value || 0));

  if (![startHour, startMinute, endHour, endMinute].every(Number.isFinite)) {
    return 0;
  }

  const startTotal = startHour * 60 + startMinute;
  const endTotal = endHour * 60 + endMinute;

  if (endTotal <= startTotal) return 0;
  return endTotal - startTotal;
}

export async function fetchCrmTicketOptions(organizationId) {
  const [
    clientsResponse,
    ticketTypesResponse,
    statusesResponse,
    projectsResponse,
    stagesResponse,
    profilesResponse,
    rolesResponse,
  ] = await Promise.all([
    fetchAllRows(() => supabase.from('clients').select('id, full_name, doc_number, is_active').eq('organization_id', organizationId).order('full_name', { ascending: true }), { label: 'ticketOptionsClients' }),
    supabase.from('crm_ticket_types').select('id, name, category_id, default_priority').eq('organization_id', organizationId).eq('is_active', true).order('name', { ascending: true }),
    supabase.from('crm_ticket_statuses').select('id, code, name, color, sort_order, is_closed').eq('organization_id', organizationId).eq('is_active', true).order('sort_order', { ascending: true }),
    fetchAllRows(() => supabase.from('crm_projects').select('id, client_id, project_type_id, name, status, estimated_total_minutes, consumed_total_minutes, is_active').eq('organization_id', organizationId).order('name', { ascending: true }), { label: 'ticketOptionsProyectos' }),
    fetchAllRows(() => supabase.from('crm_project_stages').select('id, project_id, name, stage_order, status').eq('organization_id', organizationId).order('stage_order', { ascending: true }), { label: 'ticketOptionsEtapas' }),
    fetchAllRows(() => supabase.from('profiles').select('id, user_id, full_name, email').eq('organization_id', organizationId).order('full_name', { ascending: true }), { label: 'ticketOptionsProfiles' }),
    supabase.from('crm_roles').select('id, name').eq('organization_id', organizationId).eq('is_active', true).order('name', { ascending: true }),
  ]);

  const ticketClients = unwrapResponse('clients', clientsResponse) || [];

  return {
    clients: ticketClients,
    ticketTypes: unwrapResponse('ticketTypes', ticketTypesResponse) || [],
    statuses: unwrapResponse('statuses', statusesResponse) || [],
    // Solo proyectos seleccionables: oculta los dados de baja y los de clientes
    // inactivos para que no se generen tickets nuevos sobre ellos.
    projects: filterSelectableCrmProjects(unwrapResponse('projects', projectsResponse) || [], ticketClients),
    projectStages: unwrapResponse('projectStages', stagesResponse) || [],
    profiles: unwrapResponse('profiles', profilesResponse) || [],
    roles: unwrapOptionalRows('roles', rolesResponse, 'crm_roles'),
  };
}

export async function fetchCrmTickets(organizationId) {
  // Antes tenia .limit(1000) "porque PostgREST corta ahi igual". Ya no corta:
  // fetchAllRows pagina. Sin sacarlo, el KPI Total del board quedaba topado en
  // 1000 mientras las tareas activas, que si se paginaron, contaban todas.
  const response = await fetchAllRows(
    () => supabase
      .from('crm_tickets')
      .select('*')
      .eq('organization_id', organizationId)
      .order('ticket_number', { ascending: false }),
    { label: 'crmTickets' },
  );

  const tickets = unwrapResponse('tickets', response) || [];
  const ticketIds = [...new Set(tickets.map((row) => row.id).filter(Boolean))];
  // Chunkeado ademas de paginado: `ticket_id=in.(...)` viaja en la URL y con
  // mas de ~400 UUIDs el request se cae.
  const assignmentsResponse = await fetchRowsByIds(
    (chunk) => supabase
      .from('crm_ticket_assignments')
      .select('*')
      .eq('organization_id', organizationId)
      .in('ticket_id', chunk)
      .neq('status', 'cancelled'),
    ticketIds,
    { label: 'ticketAssignments' },
  );

  const assignments = assignmentsResponse.error && isMissingRelationError(assignmentsResponse.error, 'crm_ticket_assignments')
    ? []
    : (unwrapResponse('ticketAssignments', assignmentsResponse) || []);
  const assignmentsByTicket = groupBy(assignments, 'ticket_id');

  return tickets.map((ticketRow) => ({
    ...ticketRow,
    assignments: assignmentsByTicket[ticketRow.id]
      || (ticketRow.assigned_to ? [{
        ticket_id: ticketRow.id,
        profile_id: ticketRow.assigned_to,
        is_primary: true,
        status: 'assigned',
      }] : []),
  }));
}

export async function fetchCrmTicketTasks(ticketId, organizationId = null) {
  if (!ticketId) return [];

  // organizationId es opcional (defensa en profundidad sobre RLS): cuando el
  // caller lo tiene a mano, acota todas las queries a la organizacion.
  let tasksQuery = supabase
    .from('crm_ticket_tasks')
    .select('*')
    .eq('ticket_id', ticketId)
    .order('sequence_order', { ascending: true })
    .order('created_at', { ascending: true });
  if (organizationId) tasksQuery = tasksQuery.eq('organization_id', organizationId);

  const tasksResponse = await tasksQuery;

  const tasks = unwrapResponse('ticketTasks', tasksResponse) || [];
  const taskIds = [...new Set(tasks.map((row) => row.id).filter(Boolean))];
  let assignmentsQuery = taskIds.length
    ? supabase
        .from('crm_ticket_task_assignments')
        .select('*')
        .in('ticket_task_id', taskIds)
        .neq('status', 'cancelled')
    : null;
  if (assignmentsQuery && organizationId) assignmentsQuery = assignmentsQuery.eq('organization_id', organizationId);
  let finalizationsQuery = taskIds.length
    ? supabase
        .from('crm_ticket_task_finalizations')
        .select('*')
        .in('ticket_task_id', taskIds)
        .order('finalized_at', { ascending: false })
    : null;
  if (finalizationsQuery && organizationId) finalizationsQuery = finalizationsQuery.eq('organization_id', organizationId);

  const assignmentsResponse = assignmentsQuery ? await assignmentsQuery : { data: [], error: null };
  const finalizationsResponse = finalizationsQuery ? await finalizationsQuery : { data: [], error: null };

  const assignments = unwrapResponse('ticketTaskAssignments', assignmentsResponse) || [];
  const finalizations = unwrapResponse('ticketTaskFinalizations', finalizationsResponse) || [];
  const assignmentsByTask = groupBy(assignments, 'ticket_task_id');
  const finalizationsByTask = groupBy(finalizations, 'ticket_task_id');

  return tasks.map((taskRow) => ({
    ...taskRow,
    assignments: assignmentsByTask[taskRow.id] || [],
    finalizations: finalizationsByTask[taskRow.id] || [],
  }));
}

export async function createCrmTicket(payload) {
  return createCrmTicketCompatible(payload);
}

/**
 * Historial de tareas del CRM, con paginacion real por cursor.
 *
 * Antes se cortaba a las 500 entradas mas recientes sin forma de pedir mas
 * (TODO reconocido en el codigo): una organizacion con volumen perdia el
 * historial mas viejo sin ningun aviso. Ahora `options.before` pide la
 * pagina siguiente (mas antigua) y el resultado dice si queda mas para traer.
 *
 * @param {string} organizationId
 * @param {Array<string>|null} taskIds  null = todas las tareas de la organizacion
 * @param {object} options
 * @param {number} options.limit  tamano de pagina (default 500)
 * @param {{changedAt: string, id: string}|null} options.before  cursor: trae
 *   entradas ESTRICTAMENTE anteriores a esta fila (mismo orden changed_at desc,
 *   id desc que devuelve la pagina anterior)
 * @returns {Promise<{rows: Array, hasMore: boolean}>}
 */
export async function fetchCrmTaskHistory(organizationId, taskIds = null, options = {}) {
  const { limit = 500, before = null } = options;

  if (!organizationId) return { rows: [], hasMore: false };

  const resolvedTaskIds = taskIds == null ? null : [...new Set((taskIds || []).filter(Boolean))];
  if (resolvedTaskIds && !resolvedTaskIds.length) return { rows: [], hasMore: false };

  let query = supabase
    .from('status_history')
    .select('*')
    .eq('organization_id', organizationId)
    .eq('entity_type', CRM_TASK_HISTORY_ENTITY_TYPE)
    .order('changed_at', { ascending: false })
    .order('id', { ascending: false })
    .limit(limit);

  if (resolvedTaskIds) {
    query = query.in('entity_id', resolvedTaskIds);
  }

  // Cursor por tupla (changed_at, id): "estrictamente antes que la ultima fila
  // de la pagina anterior", en el mismo orden en que se pagina. PostgREST no
  // tiene un operador de tupla directo, asi que se expresa como
  // `changed_at < cursor OR (changed_at = cursor AND id < cursorId)`.
  if (before?.changedAt && before?.id) {
    // Valores entre comillas dobles: changed_at trae ':' '.' y '+' (offset), que
    // dentro de un or() de PostgREST conviene no dejar sueltos.
    query = query.or(`changed_at.lt."${before.changedAt}",and(changed_at.eq."${before.changedAt}",id.lt.${before.id})`);
  }

  const response = await query;
  const rows = unwrapResponse('crmTaskHistory', response) || [];

  return { rows, hasMore: rows.length === limit };
}

/**
 * Registra entradas de historial de tareas del CRM (best-effort a proposito:
 * un fallo aca no debe tirar abajo el flujo principal que ya tuvo exito,
 * como una finalizacion o un update de tarea).
 *
 * Devuelve `{ data, error }` en vez de solo el array insertado: el caller
 * decide si avisar en la UI que el historial quedo incompleto (antes este
 * fallo solo se logueaba en consola y nadie se enteraba).
 */
export async function logCrmTaskHistoryEntries(entries) {
  const rows = (entries || [])
    .filter((entry) => entry?.organizationId && entry?.taskId && entry?.fieldName)
    .map((entry) => ({
      organization_id: entry.organizationId,
      entity_type: CRM_TASK_HISTORY_ENTITY_TYPE,
      entity_id: entry.taskId,
      field_name: entry.fieldName,
      from_value: stringifyHistoryValue(entry.fromValue),
      to_value: stringifyHistoryValue(entry.toValue),
      changed_by: entry.changedBy || null,
      metadata: entry.metadata || {},
    }));

  if (!rows.length) return { data: [], error: null };

  try {
    const response = await supabase.from('status_history').insert(rows).select();
    return { data: unwrapResponse('crmTaskHistoryInsert', response) || [], error: null };
  } catch (error) {
    console.error('[crmWorkflow] logCrmTaskHistoryEntries fallo (historial NO registrado):', {
      message: error?.message || String(error),
      taskIds: rows.map((row) => row.entity_id),
      fields: rows.map((row) => row.field_name),
    });
    return { data: [], error };
  }
}

export async function completeCrmTicketTask(ticketTaskId, consumedMinutes) {
  const response = await supabase.rpc('crm_complete_ticket_task', {
    p_ticket_task_id: ticketTaskId,
    p_consumed_minutes: consumedMinutes ?? null,
  });
  return unwrapResponse('completeTicketTask', response);
}

export async function completeCrmTaskAssignment(payload) {
  const {
    organizationId,
    ticketTaskId,
    taskAssignmentId,
    profileId,
    consumedMinutes = 0,
    notes,
    assignmentConsumedMinutes = 0,
    assignmentStartedAt = null,
    actorProfileId = null,
    timeEntryPayload = null,
  } = payload || {};

  const normalizedMinutes = Math.max(Number(consumedMinutes) || 0, 0);
  const actorId = actorProfileId || profileId || null;
  const nowIso = new Date().toISOString();

  // E15 · Sin transaccion del lado del cliente, el orden de los pasos define la
  // consistencia: primero se insertan la finalizacion y la hora (con rollback
  // compensatorio si un paso posterior falla) y el update del assignment a
  // 'completed' queda como ULTIMO paso, asi nunca queda una asignacion cerrada
  // con datos parciales.

  const currentResponse = await supabase
    .from('crm_ticket_task_assignments')
    .select('id, status')
    .eq('id', taskAssignmentId)
    .eq('ticket_task_id', ticketTaskId)
    .maybeSingle();

  if (currentResponse.error) {
    throw normalizeCrmError(currentResponse.error, 'No se pudo finalizar la asignacion');
  }
  const currentAssignment = currentResponse.data;
  if (!currentAssignment || ['completed', 'cancelled'].includes(String(currentAssignment.status || '').toLowerCase())) {
    throw normalizeCrmError(null, 'La asignacion ya estaba finalizada o ya no existe');
  }

  // Paso 1: registrar la finalizacion.
  const insertResponse = await supabase
    .from('crm_ticket_task_finalizations')
    .insert({
      organization_id: organizationId,
      ticket_task_id: ticketTaskId,
      task_assignment_id: taskAssignmentId,
      profile_id: profileId,
      finalized_at: nowIso,
      minutes_spent: normalizedMinutes,
      notes: notes?.trim() || null,
      created_by: actorId,
      updated_by: actorId,
    })
    .select()
    .single();

  const insertedRow = unwrapResponse('completeTaskAssignmentFinalization', insertResponse);

  const rollbackFinalization = async () => {
    if (!insertedRow?.id) return;
    try {
      await supabase.from('crm_ticket_task_finalizations').delete().eq('id', insertedRow.id);
    } catch (rollbackError) {
      console.warn('crm finalization rollback failed:', rollbackError?.message || rollbackError);
    }
  };

  // Paso 2: registrar la hora asociada (si aplica).
  let createdTimeEntry = null;
  if (timeEntryPayload && normalizedMinutes > 0) {
    try {
      createdTimeEntry = await createCrmTimeEntry(timeEntryPayload);
    } catch (timeEntryError) {
      await rollbackFinalization();
      throw timeEntryError;
    }
  }

  const rollbackTimeEntry = async () => {
    if (!createdTimeEntry?.time_entry_id) return;
    try {
      await supabase.from('crm_time_entries').delete().eq('id', createdTimeEntry.time_entry_id);
      const rollbackOrgId = timeEntryPayload?.organization_id || organizationId || null;
      const rollbackProjectId = createdTimeEntry.project_id || timeEntryPayload?.project_id || null;
      if (rollbackOrgId && rollbackProjectId) {
        await recalcCrmProjectConsumption(rollbackOrgId, rollbackProjectId, { updatedBy: actorId });
      }
    } catch (rollbackError) {
      console.warn('crm time entry rollback failed:', rollbackError?.message || rollbackError);
    }
  };

  // Paso 3: sincronizar el rollup de la tarea desde las finalizaciones.
  const rollupResponse = await supabase.rpc('crm_sync_ticket_task_assignment_rollup', {
    p_ticket_task_id: ticketTaskId,
  });
  if (rollupResponse.error) {
    await rollbackTimeEntry();
    await rollbackFinalization();
    throw normalizeCrmError(rollupResponse.error, 'No se pudo sincronizar el consumo de la tarea');
  }

  // Paso 4 (ULTIMO): marcar el assignment como completado. El filtro por
  // status protege contra un cierre concurrente entre el chequeo inicial y aca.
  const assignmentResponse = await supabase
    .from('crm_ticket_task_assignments')
    .update({
      status: 'completed',
      started_at: assignmentStartedAt || nowIso,
      completed_at: nowIso,
      consumed_minutes: normalizedMinutes > 0 ? normalizedMinutes : Math.max(Number(assignmentConsumedMinutes) || 0, 0),
      updated_by: actorId,
    })
    .eq('id', taskAssignmentId)
    .eq('ticket_task_id', ticketTaskId)
    .neq('status', 'completed')
    .neq('status', 'cancelled')
    .select()
    .maybeSingle();

  if (assignmentResponse.error || !assignmentResponse.data) {
    await rollbackTimeEntry();
    await rollbackFinalization();
    try {
      await supabase.rpc('crm_sync_ticket_task_assignment_rollup', { p_ticket_task_id: ticketTaskId });
    } catch (resyncError) {
      console.warn('crm task rollup resync failed:', resyncError?.message || resyncError);
    }
    if (assignmentResponse.error) {
      throw normalizeCrmError(assignmentResponse.error, 'No se pudo finalizar la asignacion');
    }
    throw normalizeCrmError(null, 'La asignacion ya estaba finalizada o ya no existe');
  }

  const completedAssignment = assignmentResponse.data;

  // Historial: best-effort, recien cuando todo quedo consistente.
  await logCrmTaskHistoryEntries([{
    organizationId,
    taskId: ticketTaskId,
    fieldName: 'finalization',
    toValue: notes?.trim() || `${normalizedMinutes} minutos`,
    changedBy: actorId,
    metadata: {
      profile_id: profileId,
      task_assignment_id: taskAssignmentId,
      finalization_id: insertedRow?.id || null,
      minutes_spent: normalizedMinutes,
      notes: notes?.trim() || null,
    },
  }]);

  return {
    assignment: completedAssignment,
    finalization: insertedRow,
  };
}

export async function assignCrmTicketTask(ticketTaskId, profileIds, replaceExisting = false) {
  const response = await supabase.rpc('crm_assign_ticket_task', {
    p_ticket_task_id: ticketTaskId,
    p_profile_ids: profileIds,
    p_replace_existing: replaceExisting,
  });
  return unwrapResponse('assignTicketTask', response);
}

export async function createCrmTaskAssignmentFinalization(payload) {
  const {
    organizationId,
    ticketTaskId,
    taskAssignmentId,
    profileId,
    minutesSpent,
    notes,
    assignmentConsumedMinutes = 0,
    actorProfileId = null,
    timeEntryPayload = null,
  } = payload || {};

  const normalizedMinutes = Math.max(Number(minutesSpent) || 0, 0);
  const actorId = actorProfileId || profileId || null;
  const nowIso = new Date().toISOString();

  const insertResponse = await supabase
    .from('crm_ticket_task_finalizations')
    .insert({
      organization_id: organizationId,
      ticket_task_id: ticketTaskId,
      task_assignment_id: taskAssignmentId,
      profile_id: profileId,
      finalized_at: nowIso,
      minutes_spent: normalizedMinutes,
      notes: notes?.trim() || null,
      created_by: actorId,
      updated_by: actorId,
    })
    .select()
    .single();

  const insertedRow = unwrapResponse('createTaskAssignmentFinalization', insertResponse);

  await logCrmTaskHistoryEntries([{
    organizationId,
    taskId: ticketTaskId,
    fieldName: 'finalization',
    toValue: notes?.trim() || `${normalizedMinutes} minutos`,
    changedBy: actorId,
    metadata: {
      profile_id: profileId,
      task_assignment_id: taskAssignmentId,
      finalization_id: insertedRow?.id || null,
      minutes_spent: normalizedMinutes,
      notes: notes?.trim() || null,
    },
  }]);

  const assignmentResponse = await supabase
    .from('crm_ticket_task_assignments')
    .update({
      status: 'in_progress',
      started_at: nowIso,
      consumed_minutes: Math.max(Number(assignmentConsumedMinutes) || 0, 0) + normalizedMinutes,
      updated_by: actorId,
    })
    .eq('id', taskAssignmentId)
    .neq('status', 'completed')
    .neq('status', 'cancelled')
    .select()
    .maybeSingle();

  if (assignmentResponse.error) {
    throw normalizeCrmError(assignmentResponse.error, 'No se pudo actualizar la asignacion');
  }

  const rollupResponse = await supabase.rpc('crm_sync_ticket_task_assignment_rollup', {
    p_ticket_task_id: ticketTaskId,
  });
  unwrapResponse('syncTicketTaskAssignmentRollup', rollupResponse);

  if (timeEntryPayload && normalizedMinutes > 0) {
    await createCrmTimeEntry(timeEntryPayload);
  }

  return insertedRow;
}

export async function reopenCrmTaskAssignment(payload) {
  const {
    ticketTaskId,
    taskAssignmentId,
    actorProfileId = null,
  } = payload || {};

  const assignmentResponse = await supabase
    .from('crm_ticket_task_assignments')
    .update({
      status: 'in_progress',
      completed_at: null,
      updated_by: actorProfileId,
    })
    .eq('id', taskAssignmentId)
    .eq('ticket_task_id', ticketTaskId)
    .eq('status', 'completed')
    .select()
    .maybeSingle();

  if (assignmentResponse.error) {
    throw normalizeCrmError(assignmentResponse.error, 'No se pudo reabrir la asignacion');
  }

  const reopenedAssignment = assignmentResponse.data;
  if (!reopenedAssignment) {
    throw normalizeCrmError(null, 'La asignacion no estaba finalizada o ya no existe');
  }

  const rollupResponse = await supabase.rpc('crm_sync_ticket_task_assignment_rollup', {
    p_ticket_task_id: ticketTaskId,
  });
  unwrapResponse('syncTicketTaskAssignmentRollup', rollupResponse);

  return reopenedAssignment;
}

export async function createCrmTimeEntry(payload) {
  const response = await supabase.rpc('crm_create_time_entry', { p_payload: payload });
  if (!response.error) {
    return response.data;
  }

  if (shouldFallbackCrmRpc(response.error, 'crm_create_time_entry')) {
    return createCrmTimeEntryCompatible(payload);
  }

  return unwrapResponse('createTimeEntry', response);
}

export async function fetchCrmMeetingOptions(organizationId) {
  const [clientsResponse, profilesResponse, ticketsResponse, projectsResponse] = await Promise.all([
    fetchAllRows(() => supabase.from('clients').select('id, full_name, is_active').eq('organization_id', organizationId).order('full_name', { ascending: true }), { label: 'meetingClients' }),
    fetchAllRows(() => supabase.from('profiles').select('id, full_name, email').eq('organization_id', organizationId).order('full_name', { ascending: true }), { label: 'optionsProfiles' }),
    fetchAllRows(() => supabase.from('crm_tickets').select('id, ticket_number, title, client_id, project_id').eq('organization_id', organizationId).order('ticket_number', { ascending: false }), { label: 'meetingTickets' }),
    fetchAllRows(() => supabase.from('crm_projects').select('id, client_id, code, name, status, is_active').eq('organization_id', organizationId).order('name', { ascending: true }), { label: 'meetingProjects' }),
  ]);

  const meetingClients = unwrapResponse('meetingClients', clientsResponse) || [];

  return {
    clients: meetingClients,
    profiles: unwrapResponse('meetingProfiles', profilesResponse) || [],
    tickets: unwrapResponse('meetingTickets', ticketsResponse) || [],
    // Solo proyectos seleccionables (baja del proyecto o de su cliente => ocultos).
    projects: filterSelectableCrmProjects(unwrapResponse('meetingProjects', projectsResponse) || [], meetingClients),
  };
}

export async function fetchCrmMeetings(organizationId) {
  const [meetingsResponse, participantsResponse, topicsResponse, topicParticipantsResponse] = await Promise.all([
    fetchAllRows(() => supabase.from('crm_meetings').select('*').eq('organization_id', organizationId).order('meeting_date', { ascending: false }).order('start_time', { ascending: false }), { label: 'meetings' }),
    fetchAllRows(() => supabase.from('crm_meeting_participants').select('*').eq('organization_id', organizationId).order('created_at', { ascending: true }), { label: 'meetingParticipants' }),
    fetchAllRows(() => supabase.from('crm_meeting_topics').select('*').eq('organization_id', organizationId).order('topic_order', { ascending: true }), { label: 'meetingTopics' }),
    fetchAllRows(() => supabase.from('crm_meeting_topic_participants').select('*').eq('organization_id', organizationId).order('created_at', { ascending: true }), { label: 'meetingTopicParticipants' }),
  ]);

  const meetings = unwrapResponse('meetings', meetingsResponse) || [];
  const participants = unwrapResponse('meetingParticipants', participantsResponse) || [];
  const topics = unwrapResponse('meetingTopics', topicsResponse) || [];
  const topicParticipants = topicParticipantsResponse.error && isMissingRelationError(topicParticipantsResponse.error, 'crm_meeting_topic_participants')
    ? []
    : (unwrapResponse('meetingTopicParticipants', topicParticipantsResponse) || []);

  const participantsByMeeting = participants.reduce((accumulator, row) => {
    accumulator[row.meeting_id] = accumulator[row.meeting_id] || [];
    accumulator[row.meeting_id].push(row);
    return accumulator;
  }, {});

  const topicParticipantsByTopic = groupBy(topicParticipants, 'meeting_topic_id');
  const topicsByMeeting = topics.reduce((accumulator, row) => {
    accumulator[row.meeting_id] = accumulator[row.meeting_id] || [];
    accumulator[row.meeting_id].push({
      ...row,
      topic_participants: topicParticipantsByTopic[row.id] || [],
    });
    return accumulator;
  }, {});

  return meetings.map((meeting) => ({
    ...meeting,
    participants: participantsByMeeting[meeting.id] || [],
    topics: topicsByMeeting[meeting.id] || [],
  }));
}

export async function fetchCrmMeetingDetail(organizationId, meetingId) {
  if (!organizationId || !meetingId) return null;

  const [meetingResponse, participantsResponse, topicsResponse] = await Promise.all([
    supabase.from('crm_meetings').select('*').eq('organization_id', organizationId).eq('id', meetingId).maybeSingle(),
    supabase.from('crm_meeting_participants').select('*').eq('organization_id', organizationId).eq('meeting_id', meetingId).order('created_at', { ascending: true }),
    supabase.from('crm_meeting_topics').select('*').eq('organization_id', organizationId).eq('meeting_id', meetingId).order('topic_order', { ascending: true }),
  ]);

  const meeting = unwrapResponse('meetingDetail', meetingResponse);
  if (!meeting) return null;

  const participants = unwrapResponse('meetingDetailParticipants', participantsResponse) || [];
  const topics = unwrapResponse('meetingDetailTopics', topicsResponse) || [];
  const topicIds = [...new Set(topics.map((topicRow) => topicRow.id).filter(Boolean))];
  const topicParticipantsResponse = topicIds.length
    ? await supabase
        .from('crm_meeting_topic_participants')
        .select('*')
        .eq('organization_id', organizationId)
        .in('meeting_topic_id', topicIds)
        .order('created_at', { ascending: true })
    : { data: [], error: null };
  const topicParticipants = topicParticipantsResponse.error && isMissingRelationError(topicParticipantsResponse.error, 'crm_meeting_topic_participants')
    ? []
    : (unwrapResponse('meetingDetailTopicParticipants', topicParticipantsResponse) || []);
  const topicParticipantsByTopic = groupBy(topicParticipants, 'meeting_topic_id');

  return {
    ...meeting,
    participants,
    topics: topics.map((topicRow) => ({
      ...topicRow,
      topic_participants: topicParticipantsByTopic[topicRow.id] || [],
    })),
  };
}

export async function createCrmMeeting(payload) {
  const response = await supabase.rpc('crm_create_meeting', { p_payload: payload });
  if (!response.error) {
    return response.data;
  }

  if (shouldFallbackCrmRpc(response.error, 'crm_create_meeting')) {
    return createCrmMeetingCompatible(payload);
  }

  return unwrapResponse('createMeeting', response);
}

export async function updateCrmMeeting(meetingId, payload) {
  const response = await supabase
    .from('crm_meetings')
    .update(payload)
    .eq('id', meetingId)
    .select()
    .single();

  return unwrapResponse('updateMeeting', response);
}

export async function replaceCrmMeetingParticipants({
  organizationId,
  meetingId,
  profileIds,
  actorProfileId = null,
}) {
  if (!organizationId || !meetingId) return [];

  const nextProfileIds = uniqueIds(profileIds);
  const existingResponse = await supabase
    .from('crm_meeting_participants')
    .select('id, profile_id')
    .eq('organization_id', organizationId)
    .eq('meeting_id', meetingId);

  const existingRows = unwrapResponse('meetingParticipantsExisting', existingResponse) || [];
  const rowsToDelete = existingRows
    .filter((row) => !nextProfileIds.includes(row.profile_id))
    .map((row) => row.id);

  if (rowsToDelete.length) {
    const deleteResponse = await supabase
      .from('crm_meeting_participants')
      .delete()
      .in('id', rowsToDelete);

    unwrapResponse('meetingParticipantsDelete', deleteResponse);
  }

  if (!nextProfileIds.length) {
    return [];
  }

  const upsertResponse = await supabase
    .from('crm_meeting_participants')
    .upsert(
      nextProfileIds.map((profileId) => ({
        organization_id: organizationId,
        meeting_id: meetingId,
        profile_id: profileId,
        attendance_status: 'scheduled',
        auto_log_time: true,
        created_by: actorProfileId || null,
        updated_by: actorProfileId || null,
      })),
      { onConflict: 'meeting_id,profile_id' },
    )
    .select();

  return unwrapResponse('meetingParticipantsUpsert', upsertResponse) || [];
}

export async function replaceCrmMeetingTopicParticipants({
  organizationId,
  meetingTopicId,
  profileIds,
  actorProfileId = null,
}) {
  if (!organizationId || !meetingTopicId) return [];

  const nextProfileIds = uniqueIds(profileIds);
  const existingResponse = await supabase
    .from('crm_meeting_topic_participants')
    .select('id, profile_id')
    .eq('organization_id', organizationId)
    .eq('meeting_topic_id', meetingTopicId);

  const existingRows = existingResponse.error && isMissingRelationError(existingResponse.error, 'crm_meeting_topic_participants')
    ? []
    : (unwrapResponse('meetingTopicParticipantsExisting', existingResponse) || []);

  const rowsToDelete = existingRows
    .filter((row) => !nextProfileIds.includes(row.profile_id))
    .map((row) => row.id);

  if (rowsToDelete.length) {
    const deleteResponse = await supabase
      .from('crm_meeting_topic_participants')
      .delete()
      .in('id', rowsToDelete);

    unwrapResponse('meetingTopicParticipantsDelete', deleteResponse);
  }

  if (!nextProfileIds.length) {
    return [];
  }

  const upsertResponse = await supabase
    .from('crm_meeting_topic_participants')
    .upsert(
      nextProfileIds.map((profileId) => ({
        organization_id: organizationId,
        meeting_topic_id: meetingTopicId,
        profile_id: profileId,
        created_by: actorProfileId || null,
        updated_by: actorProfileId || null,
      })),
      { onConflict: 'meeting_topic_id,profile_id' },
    )
    .select();

  return unwrapResponse('meetingTopicParticipantsUpsert', upsertResponse) || [];
}

export async function reopenCrmMeeting({
  organizationId,
  meetingId,
  actorProfileId = null,
}) {
  if (!organizationId || !meetingId) {
    throw normalizeCrmError(null, 'No se pudo reabrir la reunion');
  }

  const entriesResponse = await supabase
    .from('crm_time_entries')
    .select('id, project_id')
    .eq('organization_id', organizationId)
    .eq('meeting_id', meetingId);

  const meetingEntries = unwrapResponse('meetingTimeEntries', entriesResponse) || [];
  const impactedProjectIds = uniqueIds(meetingEntries.map((row) => row.project_id));

  if (meetingEntries.length) {
    const deleteResponse = await supabase
      .from('crm_time_entries')
      .delete()
      .eq('organization_id', organizationId)
      .eq('meeting_id', meetingId);

    unwrapResponse('meetingTimeEntriesDelete', deleteResponse);
  }

  await updateCrmMeeting(meetingId, {
    status: 'scheduled',
    actual_duration_minutes: null,
    ended_at: null,
    updated_by: actorProfileId || null,
  });

  await Promise.all(
    impactedProjectIds.map((projectId) => syncCrmTimeEntryRollupsCompatible({
      organizationId,
      projectId,
      updatedBy: actorProfileId || null,
    })),
  );

  return {
    meeting_id: meetingId,
    deleted_entries: meetingEntries.length,
    reopened: true,
  };
}

export async function saveCrmMeetingDraft({
  organizationId,
  meetingId,
  title,
  agendaText,
  meetingDate,
  startTime,
  endTime,
  durationMinutes,
  clientId = null,
  participantIds = [],
  topics = [],
  actorProfileId = null,
}) {
  if (!organizationId || !meetingId) {
    throw normalizeCrmError(null, 'No se pudo guardar la reunion');
  }

  await updateCrmMeeting(meetingId, {
    client_id: clientId || null,
    title: String(title || '').trim(),
    agenda_text: String(agendaText || '').trim() || null,
    meeting_date: meetingDate || null,
    start_time: startTime || null,
    end_time: endTime || null,
    duration_minutes: Math.max(Number(durationMinutes) || 0, 0),
    updated_by: actorProfileId || null,
  });

  await replaceCrmMeetingParticipants({
    organizationId,
    meetingId,
    profileIds: participantIds,
    actorProfileId,
  });

  const existingTopicsResponse = await supabase
    .from('crm_meeting_topics')
    .select('id')
    .eq('organization_id', organizationId)
    .eq('meeting_id', meetingId)
    .order('topic_order', { ascending: true });

  const existingTopics = unwrapResponse('meetingTopicsExisting', existingTopicsResponse) || [];
  const existingTopicIds = new Set(existingTopics.map((row) => row.id).filter(Boolean));
  const keptTopicIds = [];

  for (let index = 0; index < topics.length; index += 1) {
    const topic = topics[index] || {};
    const topicMinutes = topic.topic_minutes === '' || topic.topic_minutes == null
      ? null
      : Math.max(Number(topic.topic_minutes) || 0, 0);
    const topicPayload = {
      project_id: topic.project_id || null,
      title: String(topic.title || '').trim() || `Tema ${index + 1}`,
      notes: String(topic.notes || '').trim() || null,
      topic_order: index + 1,
      is_treated: Boolean(topic.is_treated),
      topic_minutes: topicMinutes && topicMinutes > 0 ? topicMinutes : null,
      updated_by: actorProfileId || null,
    };

    let resolvedTopicId = topic.id || null;

    if (resolvedTopicId && existingTopicIds.has(resolvedTopicId)) {
      const updateResponse = await supabase
        .from('crm_meeting_topics')
        .update(topicPayload)
        .eq('id', resolvedTopicId)
        .eq('organization_id', organizationId)
        .eq('meeting_id', meetingId)
        .select('id')
        .single();

      unwrapResponse('meetingTopicUpdateDraft', updateResponse);
    } else {
      const insertResponse = await supabase
        .from('crm_meeting_topics')
        .insert({
          organization_id: organizationId,
          meeting_id: meetingId,
          ...topicPayload,
          created_by: actorProfileId || null,
        })
        .select('id')
        .single();

      resolvedTopicId = unwrapResponse('meetingTopicInsertDraft', insertResponse)?.id || null;
    }

    if (!resolvedTopicId) continue;
    keptTopicIds.push(resolvedTopicId);

    await replaceCrmMeetingTopicParticipants({
      organizationId,
      meetingTopicId: resolvedTopicId,
      profileIds: topic.topic_participant_ids || [],
      actorProfileId,
    });
  }

  const topicIdsToDelete = existingTopics
    .map((row) => row.id)
    .filter((topicId) => topicId && !keptTopicIds.includes(topicId));

  if (topicIdsToDelete.length) {
    const deleteResponse = await supabase
      .from('crm_meeting_topics')
      .delete()
      .eq('organization_id', organizationId)
      .eq('meeting_id', meetingId)
      .in('id', topicIdsToDelete);

    unwrapResponse('meetingTopicsDeleteDraft', deleteResponse);
  }

  return {
    meeting_id: meetingId,
    participants_count: participantIds.length,
    topics_count: keptTopicIds.length,
  };
}

export async function saveCrmMeetingForFinalize({
  organizationId,
  meetingId,
  startTime,
  endTime,
  actorProfileId = null,
  topics = [],
}) {
  if (!organizationId || !meetingId) {
    throw normalizeCrmError(null, 'No se pudo preparar la reunion para el cierre');
  }

  await updateCrmMeeting(meetingId, {
    start_time: startTime || null,
    end_time: endTime || null,
    updated_by: actorProfileId || null,
  });

  for (const topic of topics) {
    if (!topic?.id) continue;

    const topicMinutes = topic.topic_minutes === '' || topic.topic_minutes == null
      ? null
      : Math.max(Number(topic.topic_minutes) || 0, 0);

    const topicResponse = await supabase
      .from('crm_meeting_topics')
      .update({
        project_id: topic.project_id || null,
        is_treated: Boolean(topic.is_treated),
        topic_minutes: topicMinutes && topicMinutes > 0 ? topicMinutes : null,
        updated_by: actorProfileId || null,
      })
      .eq('id', topic.id)
      .eq('organization_id', organizationId)
      .eq('meeting_id', meetingId)
      .select()
      .single();

    unwrapResponse('meetingTopicUpdate', topicResponse);

    await replaceCrmMeetingTopicParticipants({
      organizationId,
      meetingTopicId: topic.id,
      profileIds: topic.topic_participant_ids || [],
      actorProfileId,
    });
  }
}

export async function fetchCrmProjectOptions(organizationId) {
  const [clientsResponse, projectTypesResponse, projectsResponse, profilesResponse] = await Promise.all([
    fetchAllRows(() => supabase.from('clients').select('id, full_name, doc_number, email, phone, is_active').eq('organization_id', organizationId).order('full_name', { ascending: true }), { label: 'projectOptionsClients' }),
    supabase.from('crm_project_types').select('id, code, name, description, hours_period').eq('organization_id', organizationId).eq('is_active', true).order('sort_order', { ascending: true }),
    fetchAllRows(() => supabase.from('crm_projects').select('*').eq('organization_id', organizationId).order('created_at', { ascending: false }), { label: 'projectOptionsProyectos' }),
    fetchAllRows(() => supabase.from('profiles').select('id, full_name, email').eq('organization_id', organizationId).order('full_name', { ascending: true }), { label: 'optionsProfiles' }),
  ]);

  return {
    clients: unwrapResponse('projectOptionsClients', clientsResponse) || [],
    projectTypes: unwrapResponse('projectOptionsProjectTypes', projectTypesResponse) || [],
    projects: unwrapResponse('projectOptionsProjects', projectsResponse) || [],
    profiles: unwrapResponse('projectOptionsProfiles', profilesResponse) || [],
  };
}

async function resolveNextCrmProjectCode(organizationId) {
  // Numerador atomico: el RPC toma un advisory lock por organizacion y calcula
  // MAX+1 dentro de la transaccion, evitando codigos duplicados por concurrencia.
  const rpcResponse = await supabase.rpc('crm_next_project_code', { p_organization_id: organizationId });
  if (!rpcResponse.error && rpcResponse.data != null) {
    return String(rpcResponse.data);
  }
  if (rpcResponse.error && !shouldFallbackCrmRpc(rpcResponse.error, 'crm_next_project_code')) {
    throw normalizeCrmError(rpcResponse.error, 'No se pudo calcular el codigo del proyecto');
  }

  // Fallback sin RPC: MAX real sobre todos los codigos de la organizacion
  // (sin lock, best-effort; antes miraba solo los ultimos 50 por created_at).
  const latestResponse = await supabase
    .from('crm_projects')
    .select('code')
    .eq('organization_id', organizationId);

  const projects = unwrapResponse('crmProjectsCompatible', latestResponse) || [];
  const maxCode = projects.reduce((highest, row) => {
    const numericCode = Number(row?.code || 0);
    return Number.isFinite(numericCode) ? Math.max(highest, numericCode) : highest;
  }, 0);

  return String(maxCode + 1);
}

async function createCrmProjectCompatible(payload) {
  const organizationId = payload?.organization_id;
  const projectTypeId = payload?.project_type_id || null;
  const clientId = payload?.client_id || null;
  // 'por_horas' quedo discontinuado: el default de proyectos nuevos es one shot.
  const billingType = payload?.billing_type || 'precio_fijo';
  const skipDefaultTickets = payload?.skip_default_tickets === true;
  const projectCode = await resolveNextCrmProjectCode(organizationId);
  const createdAt = new Date().toISOString();

  if (!organizationId || !clientId || !projectTypeId) {
    throw new Error('Faltan datos minimos para crear el proyecto en modo compatibilidad');
  }

  const [clientResponse, projectTypeResponse, templatesResponse] = await Promise.all([
    supabase
      .from('clients')
      .select('id')
      .eq('organization_id', organizationId)
      .eq('id', clientId)
      .maybeSingle(),
    supabase
      .from('crm_project_types')
      .select('id')
      .eq('organization_id', organizationId)
      .eq('id', projectTypeId)
      .eq('is_active', true)
      .maybeSingle(),
    supabase
      .from('crm_project_stage_templates')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('project_type_id', projectTypeId)
      .eq('is_active', true)
      .order('default_order', { ascending: true }),
  ]);

  const clientRow = unwrapResponse('crmProjectClientCompatible', clientResponse);
  if (!clientRow?.id) {
    throw new Error('Cliente no encontrado para este proyecto');
  }

  const projectTypeRow = unwrapResponse('crmProjectTypeCompatible', projectTypeResponse);
  if (!projectTypeRow?.id) {
    throw new Error('Tipo de proyecto no encontrado');
  }

  const stageTemplates = unwrapResponse('crmProjectTemplatesCompatible', templatesResponse) || [];
  const baseRow = {
    organization_id: organizationId,
    client_id: clientId,
    project_type_id: projectTypeId,
    code: projectCode,
    name: String(payload?.name || '').trim(),
    description: String(payload?.description || '').trim() || null,
    estimated_total_minutes: Number(payload?.estimated_total_minutes ?? (payload?.budget_hours ? Math.round(Number(payload.budget_hours) * 60) : 0)) || 0,
    consumed_total_minutes: 0,
    status: payload?.status || 'draft',
    billing_type: billingType,
    budget_hours: payload?.budget_hours ?? (payload?.estimated_total_minutes ? Number((Number(payload.estimated_total_minutes) / 60).toFixed(2)) : null),
    budget_amount: payload?.budget_amount ?? null,
    manager_profile_id: payload?.manager_profile_id || null,
    created_at: createdAt,
    created_by: payload?.actor_profile_id || null,
    updated_by: payload?.actor_profile_id || null,
  };

  const projectVariants = [
    baseRow,
    {
      organization_id: baseRow.organization_id,
      client_id: baseRow.client_id,
      project_type_id: baseRow.project_type_id,
      code: baseRow.code,
      name: baseRow.name,
      description: baseRow.description,
      estimated_total_minutes: baseRow.estimated_total_minutes,
      consumed_total_minutes: baseRow.consumed_total_minutes,
      status: baseRow.status,
      created_at: baseRow.created_at,
      created_by: baseRow.created_by,
      updated_by: baseRow.updated_by,
    },
  ];

  let createdProject = null;
  let lastError = null;

  for (const row of projectVariants) {
    const response = await supabase.from('crm_projects').insert(row).select().single();
    if (!response.error) {
      createdProject = response.data;
      break;
    }

    lastError = response.error;
    if (
      isMissingColumnError(response.error, 'billing_type')
      || isMissingColumnError(response.error, 'budget_hours')
      || isMissingColumnError(response.error, 'budget_amount')
      || isMissingColumnError(response.error, 'manager_profile_id')
    ) {
      continue;
    }

    throw normalizeCrmError(response.error, 'No se pudo crear el proyecto');
  }

  if (!createdProject?.id) {
    throw normalizeCrmError(lastError, 'No se pudo crear el proyecto');
  }

  const defaultTicketsResponse = skipDefaultTickets
    ? { data: [], error: null }
    : await supabase
        .from('crm_project_type_default_ticket_types')
        .select('ticket_type_id, sort_order')
        .eq('organization_id', organizationId)
        .eq('project_type_id', projectTypeId)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });

  const defaultTicketLinks = skipDefaultTickets
    ? []
    : (defaultTicketsResponse.error && isMissingRelationError(defaultTicketsResponse.error, 'crm_project_type_default_ticket_types')
      ? []
      : (unwrapResponse('crmProjectTypeDefaultTickets', defaultTicketsResponse) || []));

  const defaultTicketTypeIds = skipDefaultTickets ? [] : uniqueIds(defaultTicketLinks.map((row) => row.ticket_type_id));
  const ticketTypesResponse = defaultTicketTypeIds.length
    ? await supabase
        .from('crm_ticket_types')
        .select('id, name, default_priority')
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .in('id', defaultTicketTypeIds)
    : { data: [], error: null };
  const ticketTypes = skipDefaultTickets ? [] : (unwrapResponse('crmProjectTypeDefaultTicketTypes', ticketTypesResponse) || []);
  const ticketTypeMap = Object.fromEntries(ticketTypes.map((row) => [row.id, row]));
  const createdTicketIds = [];
  let ticketsCreated = 0;

  const stageRows = stageTemplates.map((templateRow) => ({
    organization_id: organizationId,
    project_id: createdProject.id,
    stage_template_id: templateRow.id,
    name: templateRow.name,
    stage_order: templateRow.default_order,
    estimated_minutes: templateRow.default_estimated_minutes || 0,
    consumed_minutes: 0,
    status: 'pending',
    assigned_to: null,
    created_by: payload?.actor_profile_id || null,
    updated_by: payload?.actor_profile_id || null,
  }));

  if (stageRows.length) {
    const insertStagesResponse = await supabase.from('crm_project_stages').insert(stageRows).select('estimated_minutes');
    const insertedStages = unwrapResponse('crmProjectStagesCompatibleInsert', insertStagesResponse) || [];
    const estimatedTotalMinutes = insertedStages.reduce((total, row) => total + toNumber(row.estimated_minutes), 0);

    const updateResponse = await supabase
      .from('crm_projects')
      .update({
        estimated_total_minutes: estimatedTotalMinutes,
        budget_hours: baseRow.budget_hours ?? roundNumber(estimatedTotalMinutes / 60, 2),
        updated_by: payload?.actor_profile_id || null,
      })
      .eq('id', createdProject.id)
      .select()
      .single();

    createdProject = unwrapResponse('crmProjectCompatibleUpdate', updateResponse);
  }

  try {
    for (const linkRow of defaultTicketLinks) {
      if (!linkRow.ticket_type_id) continue;
      const ticketTypeRow = ticketTypeMap[linkRow.ticket_type_id];
      if (!ticketTypeRow?.id) continue;
      const createdTicket = await createCrmTicket({
        organization_id: organizationId,
        project_id: createdProject.id,
        ticket_type_id: linkRow.ticket_type_id,
        actor_profile_id: payload?.actor_profile_id || null,
        title: ticketTypeRow?.name || 'Ticket inicial',
        priority_weight: ticketTypeRow?.default_priority ?? 50,
      });
      if (createdTicket?.id) createdTicketIds.push(createdTicket.id);
      ticketsCreated += 1;
    }
  } catch (error) {
    if (createdTicketIds.length) {
      await supabase.from('crm_tickets').delete().eq('organization_id', organizationId).in('id', createdTicketIds);
    }
    await supabase.from('crm_projects').delete().eq('organization_id', organizationId).eq('id', createdProject.id);
    throw error;
  }

  return {
    project_id: createdProject.id,
    project_code: createdProject.code || projectCode,
    stages_created: stageRows.length,
    tickets_created: ticketsCreated,
    estimated_total_minutes: Number(createdProject.estimated_total_minutes || 0),
    budget_hours: createdProject.budget_hours ?? payload?.budget_hours ?? null,
    budget_amount: createdProject.budget_amount ?? payload?.budget_amount ?? null,
    billing_type: createdProject.billing_type || billingType,
  };
}

async function createCrmMeetingCompatible(payload) {
  const organizationId = payload?.organization_id;
  const actorProfileId = payload?.organizer_id || null;
  const createdAt = new Date().toISOString();

  if (!organizationId || !payload?.title || !payload?.meeting_date) {
    throw new Error('Faltan datos minimos para crear la reunion en modo compatibilidad');
  }

  const meetingResponse = await supabase
    .from('crm_meetings')
    .insert({
      organization_id: organizationId,
      client_id: payload?.client_id || null,
      title: String(payload.title || '').trim(),
      agenda_text: payload?.agenda_text || null,
      meeting_date: payload.meeting_date,
      start_time: payload?.start_time || null,
      end_time: payload?.end_time || null,
      duration_minutes: payload?.duration_minutes || 0,
      organizer_id: actorProfileId,
      status: 'scheduled',
      created_by: actorProfileId,
      updated_by: actorProfileId,
    })
    .select()
    .single();

  const meeting = unwrapResponse('crmMeetingCompatibleInsert', meetingResponse);

  const participants = (payload?.participants || []).map((participant) => ({
    organization_id: organizationId,
    meeting_id: meeting.id,
    profile_id: participant.profile_id,
    attendance_status: participant.attendance_status || 'scheduled',
    auto_log_time: participant.auto_log_time ?? true,
    created_by: actorProfileId,
    updated_by: actorProfileId,
  }));

  if (participants.length) {
    const participantsResponse = await supabase.from('crm_meeting_participants').insert(participants).select('id, profile_id');
    unwrapResponse('crmMeetingCompatibleParticipants', participantsResponse);
  }

  const topicRows = (payload?.topics || []).map((topic, index) => ({
    organization_id: organizationId,
    meeting_id: meeting.id,
    project_id: topic.project_id || null,
    ticket_id: topic.ticket_id || null,
    title: topic.title || `Tema ${index + 1}`,
    notes: topic.notes || null,
    topic_order: topic.topic_order || index + 1,
    is_treated: Boolean(topic.is_treated),
    topic_minutes: topic.topic_minutes || null,
    created_by: actorProfileId,
    updated_by: actorProfileId,
  }));

  const insertedTopics = topicRows.length
    ? (unwrapResponse(
        'crmMeetingCompatibleTopics',
        await supabase.from('crm_meeting_topics').insert(topicRows).select(),
      ) || [])
    : [];

  const topicParticipantRows = insertedTopics.flatMap((topicRow, index) =>
    (payload?.topics?.[index]?.topic_participant_ids || []).map((profileId) => ({
      organization_id: organizationId,
      meeting_topic_id: topicRow.id,
      profile_id: profileId,
      created_by: actorProfileId,
      updated_by: actorProfileId,
    })),
  );

  if (topicParticipantRows.length) {
    const topicParticipantsResponse = await supabase
      .from('crm_meeting_topic_participants')
      .upsert(topicParticipantRows, { onConflict: 'meeting_topic_id,profile_id' })
      .select('id');
    unwrapResponse('crmMeetingCompatibleTopicParticipants', topicParticipantsResponse);
  }

  return {
    meeting_id: meeting.id,
    participants_count: participants.length,
    topics_count: insertedTopics.length,
  };
}

function resolveCrmEntryMinutes(entryRow) {
  return entryRow?.minutes_spent != null
    ? toNumber(entryRow.minutes_spent)
    : Math.round(toNumber(entryRow?.hours_logged) * 60);
}

// Recalcula el consumo del proyecto (y de sus etapas) desde SUM(minutes) de
// crm_time_entries, en vez de incrementar valores leidos en memoria: cualquier
// alta/edicion/borrado de horas puede llamar a esta funcion y el rollup queda
// consistente aunque haya escrituras concurrentes.
export async function recalcCrmProjectConsumption(organizationId, projectId, { updatedBy = null } = {}) {
  if (!organizationId || !projectId) return null;

  const [entriesResponse, stagesResponse] = await Promise.all([
    /* Paginado obligatorio: de acá sale `consumed_total_minutes`, el agregado
       que después usan el listado, el detalle y Reportes como número de verdad.
       Cortado en 1000 filas, un proyecto largo quedaba con el consumo
       subvaluado en la propia tabla, y el error se propagaba a todas las
       pantallas sin que nada fallara. */
    fetchAllRows(
      () => supabase
        .from('crm_time_entries')
        .select('project_stage_id, minutes_spent, hours_logged')
        .eq('organization_id', organizationId)
        .eq('project_id', projectId),
      { label: 'recalcCrmProjectConsumption' },
    ),
    supabase
      .from('crm_project_stages')
      .select('id, consumed_minutes')
      .eq('organization_id', organizationId)
      .eq('project_id', projectId),
  ]);

  const entries = unwrapResponse('crmRecalcProjectEntries', entriesResponse) || [];
  const stages = unwrapResponse('crmRecalcProjectStages', stagesResponse) || [];

  const minutesByStage = entries.reduce((accumulator, entryRow) => {
    const stageId = entryRow?.project_stage_id;
    if (!stageId) return accumulator;
    accumulator[stageId] = (accumulator[stageId] || 0) + resolveCrmEntryMinutes(entryRow);
    return accumulator;
  }, {});

  for (const stageRow of stages) {
    const stageMinutes = minutesByStage[stageRow.id] || 0;
    if (toNumber(stageRow.consumed_minutes) === stageMinutes) continue;

    const stageUpdateResponse = await supabase
      .from('crm_project_stages')
      .update({
        consumed_minutes: stageMinutes,
        updated_by: updatedBy,
      })
      .eq('organization_id', organizationId)
      .eq('id', stageRow.id)
      .select('id')
      .maybeSingle();

    unwrapResponse('crmRecalcStageUpdate', stageUpdateResponse);
  }

  const consumedTotalMinutes = entries.reduce(
    (total, entryRow) => total + resolveCrmEntryMinutes(entryRow),
    0,
  );

  const projectUpdateResponse = await supabase
    .from('crm_projects')
    .update({
      consumed_total_minutes: consumedTotalMinutes,
      updated_by: updatedBy,
    })
    .eq('organization_id', organizationId)
    .eq('id', projectId)
    .select('id')
    .maybeSingle();

  unwrapResponse('crmRecalcProjectUpdate', projectUpdateResponse);
  return consumedTotalMinutes;
}

async function syncCrmTimeEntryRollupsCompatible({
  organizationId,
  projectId = null,
  projectStageId = null,
  updatedBy = null,
}) {
  let resolvedProjectId = projectId || null;

  if (projectStageId) {
    const stageResponse = await supabase
      .from('crm_project_stages')
      .select('id, project_id')
      .eq('organization_id', organizationId)
      .eq('id', projectStageId)
      .maybeSingle();

    const stageRow = unwrapResponse('crmTimeStageCompatible', stageResponse);
    if (stageRow?.id) {
      resolvedProjectId = stageRow.project_id || resolvedProjectId;
    }
  }

  if (!resolvedProjectId) return null;

  await recalcCrmProjectConsumption(organizationId, resolvedProjectId, { updatedBy });
  return resolvedProjectId;
}

async function createCrmTimeEntryCompatible(payload) {
  const organizationId = payload?.organization_id;
  const profileId = payload?.profile_id || null;
  const actorProfileId = payload?.actor_profile_id || payload?.created_by || payload?.updated_by || profileId || null;
  const ticketId = payload?.ticket_id || null;
  const projectId = payload?.project_id || null;
  const projectStageId = payload?.project_stage_id || null;
  const minutesSpent = Math.max(Number(payload?.minutes_spent || 0), 0);
  const entryDate = payload?.entry_date || getLocalDateKey();

  if (!organizationId || !profileId || minutesSpent <= 0) {
    throw new Error('Faltan datos minimos para registrar tiempo en modo compatibilidad');
  }

  let resolvedClientId = payload?.client_id || null;
  let resolvedProjectId = projectId;
  let resolvedProjectStageId = projectStageId;
  let billingRate = 0;
  let billable = payload?.billable ?? payload?.is_billable ?? true;

  if (ticketId) {
    const ticketResponse = await supabase
      .from('crm_tickets')
      .select('client_id, project_id, project_stage_id, billing_rate, billable_default')
      .eq('organization_id', organizationId)
      .eq('id', ticketId)
      .maybeSingle();

    const ticketRow = unwrapResponse('crmTimeTicketCompatible', ticketResponse);
    resolvedClientId = ticketRow?.client_id || resolvedClientId;
    resolvedProjectId = ticketRow?.project_id || resolvedProjectId;
    resolvedProjectStageId = ticketRow?.project_stage_id || resolvedProjectStageId;
    billingRate = toNumber(ticketRow?.billing_rate);
    billable = payload?.billable ?? payload?.is_billable ?? ticketRow?.billable_default ?? true;
  }

  if (!ticketId && resolvedProjectStageId) {
    const stageResponse = await supabase
      .from('crm_project_stages')
      .select('id, project_id')
      .eq('organization_id', organizationId)
      .eq('id', resolvedProjectStageId)
      .maybeSingle();

    const stageRow = unwrapResponse('crmTimeProjectStageCompatible', stageResponse);
    if (stageRow?.id) {
      resolvedProjectId = stageRow.project_id || resolvedProjectId;
    }
  }

  if (resolvedProjectId && !resolvedClientId) {
    const projectResponse = await supabase
      .from('crm_projects')
      .select('id, client_id')
      .eq('organization_id', organizationId)
      .eq('id', resolvedProjectId)
      .maybeSingle();

    const projectRow = unwrapResponse('crmTimeProjectCompatible', projectResponse);
    resolvedClientId = projectRow?.client_id || resolvedClientId;
  }

  const profileResponse = await supabase
    .from('profiles')
    .select('crm_internal_hourly_cost')
    .eq('organization_id', organizationId)
    .eq('id', profileId)
    .maybeSingle();
  const profileRow = unwrapResponse('crmTimeProfileCompatible', profileResponse);
  const hoursLogged = roundNumber(minutesSpent / 60, 4);
  const internalCost = toNumber(profileRow?.crm_internal_hourly_cost);
  const productionCost = roundNumber(hoursLogged * internalCost, 2);
  const generatedValue = roundNumber((billable ? hoursLogged * billingRate : 0), 2);

  const baseInsert = {
    organization_id: organizationId,
    profile_id: profileId,
    entry_date: entryDate,
    minutes_spent: minutesSpent,
    hours_logged: hoursLogged,
    origin_type: payload?.origin_type || 'manual',
    origin_id: payload?.origin_id || null,
    source_type: payload?.source_type || (payload?.origin_type === 'meeting_topic' ? 'MeetingTopic' : 'Task'),
    source_id: payload?.source_id || payload?.origin_id || null,
    client_id: resolvedClientId,
    project_id: resolvedProjectId,
    project_stage_id: resolvedProjectStageId,
    ticket_id: ticketId,
    meeting_id: payload?.meeting_id || null,
    meeting_topic_id: payload?.meeting_topic_id || null,
    description: payload?.description || null,
    billable: billable,
    is_billable: billable,
    invoiced: payload?.invoiced ?? false,
    internal_cost_snapshot: internalCost,
    billing_rate_snapshot: billingRate,
    production_cost_amount: productionCost,
    generated_value_amount: generatedValue,
    profitability_amount: roundNumber(generatedValue - productionCost, 2),
    created_by: actorProfileId,
    updated_by: actorProfileId,
  };

  const variants = [
    baseInsert,
    {
      organization_id: baseInsert.organization_id,
      profile_id: baseInsert.profile_id,
      entry_date: baseInsert.entry_date,
      minutes_spent: baseInsert.minutes_spent,
      origin_type: baseInsert.origin_type,
      origin_id: baseInsert.origin_id,
      client_id: baseInsert.client_id,
      project_id: baseInsert.project_id,
      project_stage_id: baseInsert.project_stage_id,
      ticket_id: baseInsert.ticket_id,
      meeting_id: baseInsert.meeting_id,
      meeting_topic_id: baseInsert.meeting_topic_id,
      description: baseInsert.description,
      billable: baseInsert.billable,
      invoiced: baseInsert.invoiced,
      created_by: baseInsert.created_by,
      updated_by: baseInsert.updated_by,
    },
  ];

  let inserted = null;
  let lastError = null;
  for (const row of variants) {
    const response = await supabase.from('crm_time_entries').insert(row).select().single();
    if (!response.error) {
      inserted = response.data;
      break;
    }
    lastError = response.error;
    if (
      isMissingColumnError(response.error, 'hours_logged')
      || isMissingColumnError(response.error, 'source_type')
      || isMissingColumnError(response.error, 'source_id')
      || isMissingColumnError(response.error, 'is_billable')
      || isMissingColumnError(response.error, 'internal_cost_snapshot')
      || isMissingColumnError(response.error, 'billing_rate_snapshot')
      || isMissingColumnError(response.error, 'production_cost_amount')
      || isMissingColumnError(response.error, 'generated_value_amount')
      || isMissingColumnError(response.error, 'profitability_amount')
    ) {
      continue;
    }
    throw normalizeCrmError(response.error, 'No se pudo registrar tiempo');
  }

  if (!inserted?.id) {
    throw normalizeCrmError(lastError, 'No se pudo registrar tiempo');
  }

  const syncedProjectId = await syncCrmTimeEntryRollupsCompatible({
    organizationId,
    projectId: resolvedProjectId,
    projectStageId: resolvedProjectStageId,
    updatedBy: actorProfileId,
  });

  return {
    time_entry_id: inserted.id,
    minutes_spent: inserted.minutes_spent,
    hours_logged: inserted.hours_logged ?? hoursLogged,
    project_id: inserted.project_id || syncedProjectId || resolvedProjectId || null,
    project_stage_id: inserted.project_stage_id || resolvedProjectStageId || null,
    ticket_id: inserted.ticket_id || ticketId,
    meeting_id: inserted.meeting_id || payload?.meeting_id || null,
    meeting_topic_id: inserted.meeting_topic_id || payload?.meeting_topic_id || null,
  };
}

async function finalizeCrmMeetingCompatible(meetingId, actualDurationMinutes) {
  const meeting = await fetchCrmMeetingDetail((await supabase.from('crm_meetings').select('organization_id').eq('id', meetingId).maybeSingle()).data?.organization_id, meetingId);
  if (!meeting) {
    throw new Error('Reunion no encontrada para cierre compatible');
  }

  if (['processed', 'completed'].includes(String(meeting.status || '').toLowerCase())) {
    return {
      meeting_id: meetingId,
      status: meeting.status,
      minutes_logged: meeting.actual_duration_minutes || meeting.duration_minutes || 0,
      time_entries_created: 0,
    };
  }

  const totalMinutes = Math.max(
    Number(actualDurationMinutes || meeting.actual_duration_minutes || calculateMinutesFromTimes(meeting.start_time, meeting.end_time) || meeting.duration_minutes || 0),
    1,
  );
  const treatedTopics = (meeting.topics || []).filter((topic) => topic.is_treated && (topic.project_id || topic.ticket_id));
  if (!treatedTopics.length) {
    throw new Error('Marca al menos un tema tratado con proyecto antes de procesar la reunion');
  }

  const explicitMinutes = treatedTopics.reduce((total, topic) => total + toNumber(topic.topic_minutes), 0);
  const flexibleTopics = treatedTopics.filter((topic) => !toNumber(topic.topic_minutes));
  const remainingMinutes = Math.max(totalMinutes - explicitMinutes, 0);
  const baseFlexible = flexibleTopics.length ? Math.floor(remainingMinutes / flexibleTopics.length) : 0;
  let remainder = flexibleTopics.length ? (remainingMinutes % flexibleTopics.length) : 0;
  let createdCount = 0;
  let skippedCount = 0;
  const failures = [];

  // E13 · Las horas se crean ANTES de marcar la reunion como procesada: si algo
  // falla a mitad de camino la reunion queda abierta y se puede reintentar.
  // Dedupe por (meeting_topic_id/origin_id + profile_id): las entries de reunion
  // siempre guardan meeting_id, asi el reintento no duplica las ya creadas.
  const existingEntriesResponse = await supabase
    .from('crm_time_entries')
    .select('id, profile_id, meeting_topic_id, origin_id')
    .eq('organization_id', meeting.organization_id)
    .eq('meeting_id', meetingId);

  let existingMeetingEntries = existingEntriesResponse.data || [];
  if (existingEntriesResponse.error) {
    const isMissingMeetingColumns = isMissingColumnError(existingEntriesResponse.error, 'meeting_id')
      || isMissingColumnError(existingEntriesResponse.error, 'meeting_topic_id')
      || isMissingColumnError(existingEntriesResponse.error, 'origin_id');
    if (!isMissingMeetingColumns) {
      throw normalizeCrmError(existingEntriesResponse.error, 'No se pudieron verificar las horas ya cargadas de la reunion');
    }
    // Esquema viejo sin columnas de reunion: no hay forma de dedupear, pero
    // tampoco pudieron existir entries previas de esta reunion (el insert
    // siempre incluye meeting_id y habria fallado igual).
    existingMeetingEntries = [];
  }

  const alreadyLoggedKeys = new Set(
    existingMeetingEntries.map((entryRow) => `${entryRow.meeting_topic_id || entryRow.origin_id || ''}::${entryRow.profile_id || ''}`),
  );

  for (const topic of treatedTopics) {
    const topicMinutes = toNumber(topic.topic_minutes) || (baseFlexible + (remainder > 0 ? 1 : 0));
    if (!toNumber(topic.topic_minutes) && remainder > 0) remainder -= 1;
    if (topicMinutes <= 0) continue;

    const recipientIds = uniqueIds([
      ...(meeting.participants || [])
        .filter((participant) => participant.auto_log_time !== false && participant.attendance_status !== 'absent')
        .map((participant) => participant.profile_id),
      ...((topic.topic_participants || []).map((participant) => participant.profile_id)),
    ]);

    for (const profileId of recipientIds) {
      if (alreadyLoggedKeys.has(`${topic.id}::${profileId}`)) {
        skippedCount += 1;
        continue;
      }

      try {
        await createCrmTimeEntry({
          organization_id: meeting.organization_id,
          profile_id: profileId,
          entry_date: meeting.meeting_date,
          minutes_spent: topicMinutes,
          origin_type: 'meeting_topic',
          origin_id: topic.id,
          source_type: 'MeetingTopic',
          source_id: topic.id,
          client_id: meeting.client_id,
          project_id: topic.project_id || null,
          ticket_id: topic.ticket_id,
          meeting_id: meeting.id,
          meeting_topic_id: topic.id,
          description: `${meeting.title || 'Reunion'} - ${topic.title || 'Tema'}`,
        });
        createdCount += 1;
      } catch (entryError) {
        failures.push(entryError);
      }
    }
  }

  if (failures.length) {
    const firstMessage = failures[0]?.message || 'error desconocido';
    throw new Error(
      `No se pudo procesar la reunion: fallo la carga de ${failures.length} registro(s) de tiempo `
      + `(${createdCount} creados en este intento). La reunion NO quedo marcada como procesada; `
      + `al reintentar no se duplican los registros ya creados. Detalle: ${firstMessage}`,
    );
  }

  // Recien con todas las horas creadas se marca la reunion como procesada.
  await updateCrmMeeting(meetingId, {
    status: 'processed',
    actual_duration_minutes: totalMinutes,
    ended_at: new Date().toISOString(),
    updated_by: meeting.organizer_id || null,
  });

  return {
    meeting_id: meeting.id,
    status: 'processed',
    minutes_logged: totalMinutes,
    time_entries_created: createdCount + skippedCount,
  };
}

export async function createCrmProject(payload) {
  // `hours_period` no lo conoce la RPC ni el alta compatible: se separa del
  // payload y se guarda apenas existe el proyecto.
  const { hours_period: hoursPeriodInput, ...createPayload } = payload || {};
  let hoursPeriod = normalizeHoursPeriod(hoursPeriodInput);

  let result;
  if (createPayload.skip_default_tickets === true) {
    result = await createCrmProjectCompatible(createPayload);
  } else {
    const response = await supabase.rpc('crm_create_project', { p_payload: createPayload });
    if (!response.error) {
      result = response.data;
    } else if (shouldFallbackCrmRpc(response.error, 'crm_create_project')) {
      result = await createCrmProjectCompatible(createPayload);
    } else {
      return unwrapResponse('createProject', response);
    }
  }

  const projectId = result?.project_id;
  // Altas que no eligen medición (importación de Trello, etc.) heredan la del
  // tipo de proyecto. Sin tipo configurado queda NULL y aplica la regla por
  // facturación de `resolveHoursPeriod`.
  if (!hoursPeriod && projectId && createPayload.project_type_id) {
    const typeResponse = await supabase
      .from('crm_project_types')
      .select('hours_period')
      .eq('organization_id', createPayload.organization_id)
      .eq('id', createPayload.project_type_id)
      .maybeSingle();
    hoursPeriod = normalizeHoursPeriod(typeResponse.data?.hours_period);
    // Solo vale la pena guardar lo que difiere de la regla por facturación.
    if (hoursPeriod === resolveHoursPeriod({ billing_type: createPayload.billing_type })) hoursPeriod = null;
  }
  if (hoursPeriod && projectId) {
    const periodResponse = await supabase
      .from('crm_projects')
      .update({ hours_period: hoursPeriod })
      .eq('organization_id', createPayload.organization_id)
      .eq('id', projectId);
    // El proyecto ya existe: un fallo acá no lo deshace, pero se avisa para que
    // no quede midiendo horas con el criterio equivocado sin que nadie lo sepa.
    if (periodResponse.error) {
      throw normalizeCrmError(
        periodResponse.error,
        'El proyecto se creó, pero no se pudo guardar cómo se miden sus horas. Configuralo desde la configuración del proyecto.',
      );
    }
  }

  return result;
}

export async function updateCrmProject(projectId, payload) {
  const response = await supabase
    .from('crm_projects')
    .update(payload)
    .eq('id', projectId)
    .select()
    .single();

  return unwrapResponse('updateProject', response);
}

// Reasigna el proyecto a otro cliente. Lo hace la RPC (una sola transacción):
// mueve también tickets y cargas de horas, exige cliente activo de la misma
// empresa y se niega si el proyecto ya tiene facturación emitida.
export async function changeCrmProjectClient(projectId, clientId, updatedBy = null) {
  const response = await supabase.rpc('crm_change_project_client', {
    p_project_id: projectId,
    p_client_id: clientId,
    p_updated_by: updatedBy,
  });
  return unwrapResponse('changeProjectClient', response);
}

export async function fetchCrmProjectsList(organizationId) {
  // Paginado: es la lista que renderiza CrmProyectos, y ahi las horas del mes ya
  // se traen completas. Sin esto, una empresa con mas de 1000 proyectos veria
  // el burn bien calculado sobre una lista incompleta.
  const response = await fetchAllRows(
    () => supabase
      .from('crm_projects')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false }),
    { label: 'projectsList' },
  );

  return unwrapResponse('projectsList', response) || [];
}

export async function fetchCrmProjectDetail(organizationId, projectId) {
  if (!organizationId || !projectId) return null;

  const [projectResponse, stagesResponse, statusesResponse, timeEntriesResponse] = await Promise.all([
    supabase
      .from('crm_projects')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('id', projectId)
      .maybeSingle(),
    supabase
      .from('crm_project_stages')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('project_id', projectId)
      .order('stage_order', { ascending: true }),
    supabase
      .from('crm_ticket_statuses')
      .select('id, code, name, color, sort_order')
      .eq('organization_id', organizationId)
      .eq('is_active', true)
      .order('sort_order', { ascending: true }),
    /* Paginado: un proyecto de años supera las 1000 cargas y PostgREST corta
       ahí en silencio. El detalle mostraba entonces un total de horas
       subvaluado, distinto del `consumed_total_minutes` que agrega el server.
       `fetchAllRows` agrega además el desempate por `id`: sin él, dos cargas
       con el mismo entry_date y created_at pueden cambiar de orden entre
       páginas y salir duplicadas o perderse. */
    fetchAllRows(
      () => supabase
        .from('crm_time_entries')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('project_id', projectId)
        .order('entry_date', { ascending: false })
        .order('created_at', { ascending: false }),
      { label: 'projectDetailTimeEntries' },
    ),
  ]);

  let ticketsResponse = await fetchAllRows(
    () => supabase
      .from('crm_tickets')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('project_id', projectId)
      .order('board_sort_order', { ascending: true, nullsFirst: false })
      .order('ticket_number', { ascending: true }),
    { label: 'projectDetailTickets' },
  );

  if (ticketsResponse.error && isMissingColumnError(ticketsResponse.error, 'board_sort_order')) {
    ticketsResponse = await fetchAllRows(
      () => supabase
        .from('crm_tickets')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('project_id', projectId)
        .order('ticket_number', { ascending: true }),
      { label: 'projectDetailTickets' },
    );
  }

  const project = unwrapResponse('projectDetail', projectResponse);
  const stages = unwrapResponse('projectDetailStages', stagesResponse) || [];
  const tickets = unwrapResponse('projectDetailTickets', ticketsResponse) || [];
  const ticketStatuses = unwrapResponse('projectDetailTicketStatuses', statusesResponse) || [];
  const timeEntries = unwrapResponse('projectDetailTimeEntries', timeEntriesResponse) || [];

  if (!project) return null;

  const projectTypeResponse = project.project_type_id
    ? await supabase
        .from('crm_project_types')
        .select('id, code, name, description, is_active, hours_period')
        .eq('organization_id', organizationId)
        .eq('id', project.project_type_id)
        .maybeSingle()
    : { data: null, error: null };
  const projectType = unwrapResponse('projectDetailProjectType', projectTypeResponse);

  const ticketIds = uniqueIds(tickets.map((ticketRow) => ticketRow.id));
  const taskMetricsResponse = await fetchRowsByIds(
    (chunk) => supabase
      .from('crm_ticket_tasks')
      .select('id, ticket_id, name, estimated_minutes, consumed_minutes, status, is_active, sequence_order, created_at')
      .eq('organization_id', organizationId)
      .order('ticket_id', { ascending: true })
      .order('sequence_order', { ascending: true })
      .order('created_at', { ascending: true })
      .in('ticket_id', chunk),
    ticketIds,
    { label: 'projectDetailTicketTaskMetrics' },
  );
  const taskMetricsRows = unwrapResponse('projectDetailTicketTaskMetrics', taskMetricsResponse) || [];
  const timeEntriesByTicket = groupBy(timeEntries, 'ticket_id');
  const tasksByTicket = taskMetricsRows.reduce((accumulator, taskRow) => {
    const ticketId = taskRow.ticket_id;
    if (!ticketId) return accumulator;
    if (!accumulator[ticketId]) accumulator[ticketId] = [];
    accumulator[ticketId].push(taskRow);
    return accumulator;
  }, {});
  const taskSummaryByTicket = taskMetricsRows.reduce((accumulator, taskRow) => {
    const ticketId = taskRow.ticket_id;
    if (!ticketId) return accumulator;

    if (!accumulator[ticketId]) {
      accumulator[ticketId] = {
        estimated_minutes: 0,
        consumed_minutes: 0,
        open_tasks: 0,
      };
    }

    accumulator[ticketId].estimated_minutes += Number(taskRow.estimated_minutes || 0);
    accumulator[ticketId].consumed_minutes += Number(taskRow.consumed_minutes || 0);

    if (taskRow.is_active !== false && ['active', 'blocked'].includes(String(taskRow.status || ''))) {
      accumulator[ticketId].open_tasks += 1;
    }

    return accumulator;
  }, {});

  return {
    ...project,
    projectType,
    stages,
    timeEntries,
    financialSummary: summarizeCrmTimeEntries(
      timeEntries,
      project.budget_hours || roundNumber(toNumber(project.estimated_total_minutes) / 60, 2),
    ),
    tickets: tickets.map((ticketRow) => ({
      ...ticketRow,
      tasks: tasksByTicket[ticketRow.id] || [],
      time_entries: timeEntriesByTicket[ticketRow.id] || [],
      task_summary: taskSummaryByTicket[ticketRow.id] || {
        estimated_minutes: 0,
        consumed_minutes: 0,
        open_tasks: 0,
      },
      financial_summary: summarizeCrmTimeEntries(
        timeEntriesByTicket[ticketRow.id] || [],
        ticketRow.estimated_hours,
      ),
    })),
    tasks: taskMetricsRows,
    ticketStatuses,
  };
}

export async function fetchCrmTicketDetail(organizationId, ticketId) {
  if (!organizationId || !ticketId) return null;

  const [response, timeEntriesResponse] = await Promise.all([
    supabase
      .from('crm_tickets')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('id', ticketId)
      .maybeSingle(),
    fetchAllRows(
      () => supabase
        .from('crm_time_entries')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('ticket_id', ticketId)
        .order('entry_date', { ascending: false }),
      { label: 'ticketDetailTimeEntries' },
    ),
  ]);

  const ticket = unwrapResponse('ticketDetail', response);
  if (!ticket) return null;
  const timeEntries = unwrapResponse('ticketDetailTimeEntries', timeEntriesResponse) || [];

  const assignmentsResponse = await supabase
    .from('crm_ticket_assignments')
    .select('*')
    .eq('organization_id', organizationId)
    .eq('ticket_id', ticketId)
    .neq('status', 'cancelled');

  const assignments = assignmentsResponse.error && isMissingRelationError(assignmentsResponse.error, 'crm_ticket_assignments')
    ? []
    : (unwrapResponse('ticketDetailAssignments', assignmentsResponse) || []);
  const tasks = await fetchCrmTicketTasks(ticketId, organizationId);

  return {
    ...ticket,
    timeEntries,
    financialSummary: summarizeCrmTimeEntries(timeEntries, ticket.estimated_hours),
    assignments: assignments.length
      ? assignments
      : (ticket.assigned_to ? [{
        ticket_id: ticket.id,
        profile_id: ticket.assigned_to,
        is_primary: true,
        status: 'assigned',
      }] : []),
    tasks,
  };
}

export async function createCrmTicketTask(payload) {
  const response = await supabase
    .from('crm_ticket_tasks')
    .insert(payload)
    .select()
    .single();

  return unwrapResponse('createTicketTask', response);
}

export async function finalizeCrmMeeting(meetingId, actualDurationMinutes) {
  const response = await supabase.rpc('crm_finalize_meeting', {
    p_meeting_id: meetingId,
    p_actual_duration_minutes: actualDurationMinutes ?? null,
  });
  if (!response.error) {
    return response.data;
  }

  if (shouldFallbackCrmRpc(response.error, 'crm_finalize_meeting')) {
    return finalizeCrmMeetingCompatible(meetingId, actualDurationMinutes);
  }

  return unwrapResponse('finalizeMeeting', response);
}

export async function fetchCrmRecentTasks(organizationId, profileId, limit = 8) {
  if (!organizationId || !profileId) return [];

  const response = await supabase.rpc('crm_recent_tasks', {
    p_organization_id: organizationId,
    p_profile_id: profileId,
    p_limit: limit,
  });

  if (!response.error) {
    return response.data || [];
  }

  if (!shouldFallbackCrmRpc(response.error, 'crm_recent_tasks')) {
    throw normalizeCrmError(response.error, 'No se pudieron cargar las tareas recientes');
  }

  const [assignmentsResponse, directTasksResponse, timeEntriesResponse] = await Promise.all([
    supabase
      .from('crm_ticket_task_assignments')
      .select('ticket_task_id')
      .eq('organization_id', organizationId)
      .eq('profile_id', profileId)
      .neq('status', 'cancelled'),
    supabase
      .from('crm_ticket_tasks')
      .select('id')
      .eq('organization_id', organizationId)
      .eq('assigned_to', profileId),
    supabase
      .from('crm_time_entries')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('profile_id', profileId)
      .eq('origin_type', 'ticket_task')
      .order('entry_date', { ascending: false })
      .limit(Math.max(limit * 4, 12)),
  ]);

  const assignmentRows = unwrapResponse('recentTaskAssignmentsFallback', assignmentsResponse) || [];
  const directTaskRows = unwrapResponse('recentTaskDirectFallback', directTasksResponse) || [];
  const timeEntries = unwrapResponse('recentTaskEntriesFallback', timeEntriesResponse) || [];
  const timeEntriesByTask = groupBy(timeEntries, 'origin_id');
  const taskIds = uniqueIds([
    ...assignmentRows.map((row) => row.ticket_task_id),
    ...directTaskRows.map((row) => row.id),
    ...timeEntries.map((row) => row.origin_id),
  ]);
  const taskRowsResponse = taskIds.length
    ? await supabase.from('crm_ticket_tasks').select('id, ticket_id, name, status, created_at, updated_at').eq('organization_id', organizationId).in('id', taskIds)
    : { data: [], error: null };
  const taskRows = unwrapResponse('recentTaskRowsFallback', taskRowsResponse) || [];
  const taskMap = Object.fromEntries(taskRows.map((row) => [row.id, row]));
  const linkedTicketIds = uniqueIds(taskRows.map((row) => row.ticket_id));
  const linkedTicketsResponse = linkedTicketIds.length
    ? await supabase.from('crm_tickets').select('id, ticket_number, title, project_id').eq('organization_id', organizationId).in('id', linkedTicketIds)
    : { data: [], error: null };
  const linkedTickets = unwrapResponse('recentTaskTicketsFallback', linkedTicketsResponse) || [];
  const ticketMap = Object.fromEntries(linkedTickets.map((row) => [row.id, row]));
  const linkedProjectIds = uniqueIds(linkedTickets.map((row) => row.project_id));
  const linkedProjectsResponse = linkedProjectIds.length
    ? await supabase.from('crm_projects').select('id, name').eq('organization_id', organizationId).in('id', linkedProjectIds)
    : { data: [], error: null };
  const linkedProjects = unwrapResponse('recentTaskProjectsFallback', linkedProjectsResponse) || [];
  const projectMap = Object.fromEntries(linkedProjects.map((row) => [row.id, row]));

  return taskIds
    .map((taskId) => {
      const taskRow = taskMap[taskId];
      const recentEntries = timeEntriesByTask[taskId] || [];
      const ticketRow = ticketMap[taskRow?.ticket_id];
      return {
        task_id: taskId,
        ticket_id: taskRow?.ticket_id || null,
        ticket_number: ticketRow?.ticket_number || null,
        ticket_title: ticketRow?.title || 'Ticket',
        task_name: taskRow?.name || 'Tarea',
        task_status: taskRow?.status || 'active',
        assignment_status: 'fallback',
        project_id: ticketRow?.project_id || null,
        project_name: projectMap[ticketRow?.project_id]?.name || 'Sin proyecto',
        last_activity_at: recentEntries[0]?.created_at || taskRow?.updated_at || taskRow?.created_at || null,
        last_entry_date: recentEntries[0]?.entry_date || null,
        recent_hours: roundNumber(recentEntries.reduce((total, row) => total + toNumber(row.hours_logged || toNumber(row.minutes_spent) / 60), 0), 4),
      };
    })
    .sort((left, right) => new Date(right.last_activity_at || 0).getTime() - new Date(left.last_activity_at || 0).getTime())
    .slice(0, limit);
}

export async function fetchCrmUserDashboard(organizationId, profileId) {
  const today = getLocalDateKey(new Date());
  const recentTasksPromise = fetchCrmRecentTasks(organizationId, profileId, 6);

  let profileAvailabilityQuery = supabase
    .from('profiles')
    .select('id, crm_available_hours_per_day, crm_workdays')
    .eq('organization_id', organizationId)
    .eq('id', profileId)
    .maybeSingle();

  const [legacyTasksResponse, taskAssignmentsResponse, participantsResponse, timeEntriesResponse, profileAvailabilityResponseInitial, capacityResponse] = await Promise.all([
    supabase
      .from('crm_ticket_tasks')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('assigned_to', profileId)
      .in('status', ['active', 'blocked'])
      .order('status', { ascending: true })
      .order('sequence_order', { ascending: true }),
    supabase
      .from('crm_ticket_task_assignments')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('profile_id', profileId)
      .in('status', ['assigned', 'in_progress'])
      .order('assigned_at', { ascending: true }),
    supabase
      .from('crm_meeting_participants')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('profile_id', profileId)
      .order('created_at', { ascending: true }),
    supabase
      .from('crm_time_entries')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('profile_id', profileId)
      .eq('entry_date', today)
      .order('created_at', { ascending: false }),
    profileAvailabilityQuery,
    supabase
      .from('crm_user_capacity_calendar')
      .select('profile_id, work_date, capacity_minutes, absence_type, notes')
      .eq('organization_id', organizationId)
      .eq('profile_id', profileId)
      .eq('work_date', today)
      .maybeSingle(),
  ]);

  let profileAvailabilityResponse = profileAvailabilityResponseInitial;
  if (profileAvailabilityResponse.error && (
    isMissingColumnError(profileAvailabilityResponse.error, 'crm_available_hours_per_day')
    || isMissingColumnError(profileAvailabilityResponse.error, 'crm_workdays')
  )) {
    profileAvailabilityResponse = await supabase
      .from('profiles')
      .select('id, crm_available_hours_per_day')
      .eq('organization_id', organizationId)
      .eq('id', profileId)
      .maybeSingle();

    if (profileAvailabilityResponse.error && isMissingColumnError(profileAvailabilityResponse.error, 'crm_available_hours_per_day')) {
      profileAvailabilityResponse = await supabase
        .from('profiles')
        .select('id')
        .eq('organization_id', organizationId)
        .eq('id', profileId)
        .maybeSingle();
    }
  }

  const legacyTasks = unwrapResponse('dashboardTasks', legacyTasksResponse) || [];
  const taskAssignments = unwrapResponse('dashboardTaskAssignments', taskAssignmentsResponse) || [];
  const meetingParticipants = unwrapResponse('dashboardMeetingParticipants', participantsResponse) || [];
  const timeEntries = unwrapResponse('dashboardTimeEntries', timeEntriesResponse) || [];
  const profileAvailability = unwrapResponse('dashboardProfileAvailability', profileAvailabilityResponse) || {};
  const capacityRow = capacityResponse?.error
    ? (isMissingRelationError(capacityResponse.error, 'crm_user_capacity_calendar') ? null : (() => { throw normalizeCrmError(capacityResponse.error, 'No se pudo resolver dashboardCapacity'); })())
    : (capacityResponse?.data || null);
  const configuredDailyHours = Number(profileAvailability?.crm_available_hours_per_day);
  const workdays = normalizeDashboardWorkdays(profileAvailability?.crm_workdays);
  const dailyTargetMinutes = capacityRow
    ? Math.max(0, Math.round(Number(capacityRow.capacity_minutes || 0)))
    : (
      workdays.includes(getIsoWeekday(today))
        ? Math.max(
            0,
            Math.round(
              (Number.isFinite(configuredDailyHours) && configuredDailyHours > 0
                ? configuredDailyHours * 60
                : DEFAULT_DAILY_TARGET_MINUTES),
            ),
          )
        : 0
    );
  const assignmentTaskIds = [...new Set(taskAssignments.map((row) => row.ticket_task_id).filter(Boolean))];

  const meetingIds = [...new Set(meetingParticipants.map((row) => row.meeting_id).filter(Boolean))];
  const assignmentTasksResponse = assignmentTaskIds.length
    ? await supabase
        .from('crm_ticket_tasks')
        .select('*')
        .eq('organization_id', organizationId)
        .in('id', assignmentTaskIds)
        .in('status', ['active', 'blocked'])
        .order('status', { ascending: true })
        .order('sequence_order', { ascending: true })
    : { data: [], error: null };

  const assignmentTasks = unwrapResponse('dashboardAssignmentTasks', assignmentTasksResponse) || [];
  const tasks = mergeTasksById(legacyTasks, assignmentTasks);
  const taskIds = [...new Set(tasks.map((row) => row.id).filter(Boolean))];
  const allTaskAssignmentsResponse = taskIds.length
    ? await supabase
        .from('crm_ticket_task_assignments')
        .select('*')
        .eq('organization_id', organizationId)
        .in('ticket_task_id', taskIds)
        .neq('status', 'cancelled')
    : { data: [], error: null };

  const allTaskAssignments = unwrapResponse('dashboardAllTaskAssignments', allTaskAssignmentsResponse) || [];
  const assignmentsByTask = groupBy(allTaskAssignments, 'ticket_task_id');
  const currentAssignmentsByTask = Object.fromEntries(taskAssignments.map((row) => [row.ticket_task_id, row]));
  const decoratedTasks = tasks
    .map((taskRow) => ({
      ...taskRow,
      assignments: assignmentsByTask[taskRow.id] || [],
      current_assignment: currentAssignmentsByTask[taskRow.id] || null,
    }))
    .sort((left, right) => {
      const statusCompare = String(left.status || '').localeCompare(String(right.status || ''));
      if (statusCompare !== 0) return statusCompare;
      return Number(left.sequence_order || 0) - Number(right.sequence_order || 0);
    });

  const linkedTicketIds = [...new Set(decoratedTasks.map((row) => row.ticket_id).filter(Boolean))];

  const [meetingsResponse, linkedTicketsResponse] = await Promise.all([
    meetingIds.length
      ? supabase
          .from('crm_meetings')
          .select('*')
          .eq('organization_id', organizationId)
          .in('id', meetingIds)
          .order('meeting_date', { ascending: true })
          .order('start_time', { ascending: true })
      : Promise.resolve({ data: [], error: null }),
    linkedTicketIds.length
      ? supabase
          .from('crm_tickets')
          .select('*')
          .eq('organization_id', organizationId)
          .in('id', linkedTicketIds)
      : Promise.resolve({ data: [], error: null }),
  ]);

  const meetings = unwrapResponse('dashboardMeetings', meetingsResponse) || [];
  const linkedTickets = unwrapResponse('dashboardLinkedTickets', linkedTicketsResponse) || [];
  const recentTasks = await recentTasksPromise;
  const ticketMap = Object.fromEntries(linkedTickets.map((row) => [row.id, row]));

  return {
    tickets: linkedTickets,
    tasks: decoratedTasks,
    meetings,
    meetingParticipants,
    timeEntries,
    todayDate: today,
    dailyTargetMinutes,
    recentTasks,
    linkedTickets,
    ticketMap,
  };
}

export async function fetchCrmTriageBoard(organizationId) {
  const [tasksResponse, taskAssignmentsResponse] = await Promise.all([
    supabase
      .from('crm_ticket_tasks')
      .select('*')
      .eq('organization_id', organizationId)
      .in('status', ['active', 'blocked'])
      .order('status', { ascending: true })
      .order('sequence_order', { ascending: true }),
    supabase
      .from('crm_ticket_task_assignments')
      .select('ticket_task_id, status')
      .eq('organization_id', organizationId)
      .neq('status', 'cancelled'),
  ]);

  const tasks = unwrapResponse('triageTasks', tasksResponse) || [];
  const taskAssignments = unwrapOptionalRows('triageTaskAssignments', taskAssignmentsResponse, 'crm_ticket_task_assignments');
  const assignmentsByTask = groupBy(taskAssignments, 'ticket_task_id');
  const unassignedTasks = tasks.filter((taskRow) => !taskRow.assigned_to && !(assignmentsByTask[taskRow.id] || []).length);

  const linkedTicketIds = [...new Set(unassignedTasks.map((row) => row.ticket_id).filter(Boolean))];
  const linkedTicketsResponse = linkedTicketIds.length
    ? await supabase
        .from('crm_tickets')
        .select('*')
        .eq('organization_id', organizationId)
        .in('id', linkedTicketIds)
        .order('priority_weight', { ascending: false })
        .order('reported_at', { ascending: false })
    : { data: [], error: null };

  const linkedTickets = unwrapResponse('triageLinkedTickets', linkedTicketsResponse) || [];

  return {
    tickets: linkedTickets,
    tasks: unassignedTasks,
    linkedTickets,
  };
}

export async function updateCrmTicket(ticketId, payload, organizationId = null) {
  // organizationId opcional: defensa en profundidad sobre RLS cuando el caller lo tiene.
  const runUpdate = (nextPayload) => {
    let query = supabase.from('crm_tickets').update(nextPayload).eq('id', ticketId);
    if (organizationId) query = query.eq('organization_id', organizationId);
    return query.select().single();
  };

  let response = await runUpdate(payload);
  if (response.error) {
    const missingBoardSortOrder = Object.prototype.hasOwnProperty.call(payload || {}, 'board_sort_order') && isMissingColumnError(response.error, 'board_sort_order');
    const missingTrelloCardPos = Object.prototype.hasOwnProperty.call(payload || {}, 'trello_card_pos') && isMissingColumnError(response.error, 'trello_card_pos');

    if (missingBoardSortOrder || missingTrelloCardPos) {
      const fallbackPayload = { ...(payload || {}) };
      if (missingBoardSortOrder) delete fallbackPayload.board_sort_order;
      if (missingTrelloCardPos) delete fallbackPayload.trello_card_pos;

      const meaningfulKeys = Object.keys(fallbackPayload).filter((key) => !['updated_by', 'updated_at'].includes(key));
      if (!meaningfulKeys.length) {
        throw new Error(getTicketBoardOrderMigrationMessage());
      }

      response = await runUpdate(fallbackPayload);
    }
  }

  return unwrapResponse('updateTicket', response);
}

export async function updateCrmTicketTask(ticketTaskId, payload, options = null) {
  const trackHistory = Boolean(options?.trackHistory && options?.organizationId && options?.actorUserId);
  // Defensa en profundidad: si el caller informa la organizacion (aunque no
  // pida historial), acotamos las queries a ella ademas del RLS.
  const scopedOrganizationId = options?.organizationId || null;
  let previousTask = null;

  if (trackHistory) {
    let previousQuery = supabase
      .from('crm_ticket_tasks')
      .select('id, ticket_id, name, description, status, estimated_minutes')
      .eq('id', ticketTaskId);
    if (scopedOrganizationId) previousQuery = previousQuery.eq('organization_id', scopedOrganizationId);

    previousTask = unwrapResponse('previousTicketTask', await previousQuery.maybeSingle());
  }

  let updateQuery = supabase.from('crm_ticket_tasks').update(payload).eq('id', ticketTaskId);
  if (scopedOrganizationId) updateQuery = updateQuery.eq('organization_id', scopedOrganizationId);
  const response = await updateQuery.select().single();
  const updatedTask = unwrapResponse('updateTicketTask', response);

  if (trackHistory && previousTask && updatedTask) {
    const historyEntries = [];

    if (Object.prototype.hasOwnProperty.call(payload, 'name') && previousTask.name !== updatedTask.name) {
      historyEntries.push({
        organizationId: options.organizationId,
        taskId: ticketTaskId,
        fieldName: 'name',
        fromValue: previousTask.name,
        toValue: updatedTask.name,
        changedBy: options.actorUserId,
        metadata: {
          source: options.metadata?.source || 'Tarea',
          ticket_id: previousTask.ticket_id || updatedTask.ticket_id || null,
          task_name: updatedTask.name,
        },
      });
    }

    if (Object.prototype.hasOwnProperty.call(payload, 'estimated_minutes')) {
      const previousMinutes = Number(previousTask.estimated_minutes || 0);
      const updatedMinutes = Number(updatedTask.estimated_minutes || 0);

      if (previousMinutes !== updatedMinutes) {
        historyEntries.push({
          organizationId: options.organizationId,
          taskId: ticketTaskId,
          fieldName: 'estimated_minutes',
          fromValue: previousMinutes,
          toValue: updatedMinutes,
          changedBy: options.actorUserId,
          metadata: {
            source: options.metadata?.source || 'Tarea',
            ticket_id: previousTask.ticket_id || updatedTask.ticket_id || null,
            task_name: updatedTask.name,
          },
        });
      }
    }

    if (Object.prototype.hasOwnProperty.call(payload, 'status') && previousTask.status !== updatedTask.status) {
      historyEntries.push({
        organizationId: options.organizationId,
        taskId: ticketTaskId,
        fieldName: 'status',
        fromValue: previousTask.status,
        toValue: updatedTask.status,
        changedBy: options.actorUserId,
        metadata: {
          source: options.metadata?.source || 'Tarea',
          ticket_id: previousTask.ticket_id || updatedTask.ticket_id || null,
          task_name: updatedTask.name,
        },
      });
    }

    if (Object.prototype.hasOwnProperty.call(payload, 'description') && previousTask.description !== updatedTask.description) {
      historyEntries.push({
        organizationId: options.organizationId,
        taskId: ticketTaskId,
        fieldName: 'description',
        fromValue: previousTask.description,
        toValue: updatedTask.description,
        changedBy: options.actorUserId,
        metadata: {
          source: options.metadata?.source || 'Tarea',
          ticket_id: previousTask.ticket_id || updatedTask.ticket_id || null,
          task_name: updatedTask.name,
        },
      });
    }

    if (historyEntries.length) {
      await logCrmTaskHistoryEntries(historyEntries);
    }
  }

  return updatedTask;
}

export async function assignCrmTicket(ticketId, assignedTo, updatedBy, alsoAssignActiveTasks = false) {
  const profileIds = [...new Set((Array.isArray(assignedTo) ? assignedTo : [assignedTo]).filter(Boolean))];
  const response = await supabase.rpc('crm_assign_ticket', {
    p_ticket_id: ticketId,
    p_profile_ids: profileIds,
    p_replace_existing: true,
  });
  const primaryProfileId = profileIds[0] || null;
  const ticket = response.error && (
    isMissingRelationError(response.error, 'crm_assign_ticket')
    || isMissingRelationError(response.error, 'crm_ticket_assignments')
  )
    ? await updateCrmTicket(ticketId, {
      assigned_to: primaryProfileId,
      updated_by: updatedBy || null,
    })
    : unwrapResponse('assignTicket', response);

  if (alsoAssignActiveTasks) {
    const { error } = await supabase
      .from('crm_ticket_tasks')
      .update({
        assigned_to: primaryProfileId,
        updated_by: updatedBy || null,
      })
      .eq('ticket_id', ticketId)
      .is('assigned_to', null)
      .eq('status', 'active');

    if (error) throw error;
  }

  return ticket;
}

export function getPriorityMeta(value) {
  const numeric = Number(value || 0);

  if (numeric >= 90) {
    return { label: 'Urgente', tone: 'rose' };
  }
  if (numeric >= 70) {
    return { label: 'Alta', tone: 'amber' };
  }
  if (numeric >= 40) {
    return { label: 'Media', tone: 'blue' };
  }
  return { label: 'Baja', tone: 'slate' };
}

export function formatMinutes(value) {
  const minutes = Number(value || 0);
  if (!minutes) return '0m';

  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;

  // Las horas acumuladas de un proyecto llegan facil a 4 digitos, asi que van
  // con separador de miles (1.234h). Los minutos nunca pasan de 59.
  const hoursLabel = formatNumberAr(hours, { maximumFractionDigits: 0 });

  if (!hours) return `${remaining}m`;
  if (!remaining) return `${hoursLabel}h`;
  return `${hoursLabel}h ${remaining}m`;
}

export function formatHours(value, digits = 1) {
  const hours = toNumber(value);
  if (!hours) return '0h';
  return `${formatNumberAr(hours, { minimumFractionDigits: digits, maximumFractionDigits: digits })}h`;
}

export function isProcessedMeetingStatus(status) {
  return ['processed', 'completed'].includes(String(status || '').toLowerCase());
}

export function getMeetingStatusMeta(status) {
  return isProcessedMeetingStatus(status)
    ? { label: 'Procesada', tone: 'emerald' }
    : { label: 'Programada', tone: 'blue' };
}

